import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { Icon } from '@iconify/react';
import { SegmentedControl } from '../atoms/SegmentedControl';
import { FilterBar, type GroupByKey } from '../atoms/FilterBar';
import { MultiSelectPopover } from '../atoms/MultiSelectPopover';
import { useStageReveal, collectViewBlocks } from './useViewTransition';
import { SequenceBoard, SequenceDots } from './SequenceBoard';
import { CompanyLogoImg, resolveCompanyLogoUrl } from './LeadAvatar';
import { MessagePreviewContent } from './MessagePreviewContent';
import { MessagePreviewRail } from './MessagePreviewRail';
import { uniBoxEntityToMappedLead } from './dummyData';
import { getStatusDef } from './taxonomy';
import {
  KanbanGroupHeader,
  SchemaKanbanCard,
  SchemaKanbanColumn,
  type KanbanCardDensity,
  type KanbanCardSchema,
} from './KanbanPrimitives';
import { LeadReportModal } from '../LeadReportModal';
import { ISRWorkspaceDrawer } from '../ISRWorkspace/ISRWorkspace';
import type { CampaignMetadata, Interaction, ISRNotesDocument, ISRUpdateDiff, MappedLeadData } from '../../types';
import { isLeadEnrichedForISR } from '../../utils/statusMapping';
import type {
  UniBoxApi,
  UniBoxAutomationMode,
  UniBoxConfig,
  UniBoxControlState,
  UniBoxEntity,
  UniBoxFeed,
  UniBoxGroup,
  UniBoxThread,
  UniBoxViewMode,
} from './types';

// A "lens" is the primary navigation concept: which part of the funnel you're
// working in. Each lens owns a default viewMode and the lead-status slice it
// focuses. Pipeline = the full-funnel Kanban home; Outreach = the send plan
// (outreaching leads); Inbox = conversations needing you (engaged+); All =
// everything.
export type UniBoxLens = 'pipeline' | 'outreach' | 'inbox' | 'all';

/**
 * A combined patch of view-state fields. Used by `onUniBoxChange` so a single
 * user action (e.g. clicking a lens tab, which also seeds a default view +
 * mailbox) emits ONE URL write instead of several per-field navigations that
 * close over stale search params and clobber each other.
 */
export type UniBoxViewPatch = {
  lens?: UniBoxLens;
  view?: UniBoxViewMode;
  mailbox?: 'inbox' | 'queue';
  calRange?: 'month' | 'week';
};

interface UniBoxProps {
  config: UniBoxConfig;
  api: UniBoxApi;
  /** Canonical campaign context used by the read-only ISR briefing drawer. */
  campaigns?: CampaignMetadata[];
  // URL-driven view state. When the onChange handlers are provided, the view is
  // controlled from the URL (deep-linkable); otherwise UniBox keeps local state.
  lens?: UniBoxLens;
  view?: UniBoxViewMode;
  mailbox?: 'inbox' | 'queue';
  calRange?: 'month' | 'week';
  onLensChange?: (next: UniBoxLens) => void;
  onViewChange?: (next: UniBoxViewMode) => void;
  onMailboxChange?: (next: 'inbox' | 'queue') => void;
  onCalRangeChange?: (next: 'month' | 'week') => void;
  /**
   * Batched view-state change. Preferred over the per-field callbacks when MORE
   * THAN ONE field changes in a single action — it collapses them into one URL
   * write so they can't race. Falls back to the per-field callbacks when unset.
   */
  onUniBoxChange?: (patch: UniBoxViewPatch) => void;
  /** Open a lead's full profile (Leads Intelligence detail). */
  onOpenLead?: (entity: UniBoxEntity) => void;
  /** Open a campaign's details surface (e.g. a modal). */
  onOpenCampaignDetails?: (groupId: string) => void;
  /**
   * Persist a sales status/substatus change made in the ISR drawer — SAME
   * update_campaign n8n action the discard flow uses. When omitted (hosts
   * running dummy/mock data), the drawer's stepper stays read-only.
   *
   * The updated object categorises each change by level:
   * - personLvl2 fields are applied directly
   * - company fields are SUGGESTIONS only (shared lvl1 records need approval)
   */
  onSaveSalesState?: (entity: UniBoxEntity, sales: { lead_status: string; lead_substatus: string | null }, notes: ISRNotesDocument, updated?: ISRUpdateDiff) => void | Promise<void>;
  /** Team currency (ISO-4217) for opportunity value formatting. */
  currency?: string | null;
  /** Optional controls rendered on the same top toolbar as the lens selector. */
  toolbarSlot?: React.ReactNode;
}

interface ChannelMeta {
  label: string;
  icon: string;
  accent: string;
  soft: string;
}

// Status visuals (color/dot/pill/icon) come from the taxonomy (taxonomy.ts),
// so this file no longer keeps a status→color map.

const CHANNEL_META: Record<string, ChannelMeta> = {
  whatsapp: { label: 'WhatsApp', icon: 'bx:bxl-whatsapp', accent: 'text-emerald-300', soft: 'bg-emerald-400/12' },
  email: { label: 'Email', icon: 'bx:envelope', accent: 'text-cyan-200', soft: 'bg-cyan-400/12' },
  sms: { label: 'SMS', icon: 'bx:message-rounded', accent: 'text-indigo-200', soft: 'bg-indigo-400/12' },
  call: { label: 'Call', icon: 'bx:phone-call', accent: 'text-amber-200', soft: 'bg-amber-400/12' },
  linkedin: { label: 'LinkedIn', icon: 'bx:bxl-linkedin', accent: 'text-sky-200', soft: 'bg-sky-400/12' },
  instagram: { label: 'Instagram', icon: 'bx:bxl-instagram', accent: 'text-pink-200', soft: 'bg-pink-400/12' },
  x: { label: 'X', icon: 'bx:bxl-twitter', accent: 'text-slate-100', soft: 'bg-white/[0.08]' },
  messenger: { label: 'Messenger', icon: 'bx:bxl-messenger', accent: 'text-blue-200', soft: 'bg-blue-400/12' },
  telegram: { label: 'Telegram', icon: 'bx:bxl-telegram', accent: 'text-sky-200', soft: 'bg-sky-400/12' },
};

const AUTOMATION_META: Record<UniBoxAutomationMode, { label: string; icon: string; className: string }> = {
  autopilot: { label: 'Autopilot', icon: 'bx:bot', className: 'border-cyan-300/20 bg-cyan-400/8 text-cyan-100' },
  copilot: { label: 'Copilot', icon: 'bx:user', className: 'border-amber-300/20 bg-amber-400/8 text-amber-100' },
  manual: { label: 'Manual', icon: 'bx:pause-circle', className: 'border-white/12 bg-white/[0.04] text-gray-200' },
};

const CONTROL_META: Record<UniBoxControlState, { label: string; icon: string; className: string }> = {
  ai_running: { label: 'AI running', icon: 'bx:bot', className: 'border-white/8 bg-white/[0.025] text-gray-400' },
  human_takeover: { label: 'You', icon: 'bx:user', className: 'border-teal-300/25 bg-teal-400/10 text-teal-100' },
  escalated: { label: 'Needs you', icon: 'bx:error-circle', className: 'border-amber-300/35 bg-amber-400/12 text-amber-100' },
};

const VIEW_MODE_META: Record<UniBoxViewMode, { label: string; icon: string }> = {
  inbox: { label: 'Inbox', icon: 'bx:inbox' },
  calendar: { label: 'Calendar', icon: 'bx:calendar' },
  kanban: { label: 'Kanban', icon: 'bx:columns' },
};

// Campaign/account colors intentionally avoid green/success tones and the
// canonical status palette so users do not read campaign identity as a funnel
// stage. Blue, purple, and black/graphite are the primary campaign identity
// colors; the warm tones stay as accents only, and all greens are avoided.
const GROUP_DOT = ['bg-[#1A1C23]', 'bg-[#2B303B]', 'bg-[#24364D]', 'bg-[#2F4E70]', 'bg-[#466C9A]', 'bg-[#596BA6]', 'bg-[#514378]', 'bg-[#654887]', 'bg-[#805A9C]', 'bg-[#9A6AAE]', 'bg-[#A14F48]', 'bg-[#B8A05A]'];
const GROUP_TEXT = ['text-[#C5CAD4]', 'text-[#D1D5DD]', 'text-[#9FB8D5]', 'text-[#A9C5E4]', 'text-[#B7CEF0]', 'text-[#C2CBF7]', 'text-[#C8BCEB]', 'text-[#D7BCEB]', 'text-[#E3C4F1]', 'text-[#EBCDF4]', 'text-[#E99A92]', 'text-[#E6D28A]'];
const GROUP_HEX = ['#1A1C23', '#2B303B', '#24364D', '#2F4E70', '#466C9A', '#596BA6', '#514378', '#654887', '#805A9C', '#9A6AAE', '#A14F48', '#B8A05A'];
const GROUP_SHADOW = [
  'shadow-[0_0_10px_rgba(26,28,35,0.7)]',
  'shadow-[0_0_10px_rgba(43,48,59,0.7)]',
  'shadow-[0_0_10px_rgba(36,54,77,0.7)]',
  'shadow-[0_0_10px_rgba(47,78,112,0.7)]',
  'shadow-[0_0_10px_rgba(70,108,154,0.7)]',
  'shadow-[0_0_10px_rgba(89,107,166,0.7)]',
  'shadow-[0_0_10px_rgba(81,67,120,0.7)]',
  'shadow-[0_0_10px_rgba(101,72,135,0.7)]',
  'shadow-[0_0_10px_rgba(128,90,156,0.7)]',
  'shadow-[0_0_10px_rgba(154,106,174,0.7)]',
  'shadow-[0_0_10px_rgba(161,79,72,0.7)]',
  'shadow-[0_0_10px_rgba(184,160,90,0.7)]',
];

const getGroupColorIndex = (groups: UniBoxGroup[], groupId: string) => {
  const index = groups.findIndex((group) => group.id === groupId);
  return index < 0 ? 0 : index;
};

const isGroup = (value: unknown): value is UniBoxGroup => {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  return typeof item.id === 'string' && typeof item.name === 'string' && typeof item.total === 'number' && typeof item.needs_human === 'number' && typeof item.automation_mode === 'string';
};

const isEntity = (value: unknown): value is UniBoxEntity => {
  if (!value || typeof value !== 'object') return false;
  const item = value as Record<string, unknown>;
  return typeof item.lvl1_id === 'number' && typeof item.client_name === 'string' && typeof item.status === 'string' && typeof item.substatus === 'string' && Array.isArray(item.channels);
};

const getFeedCollections = (feed: UniBoxFeed) => {
  const arrays = Object.values(feed).filter(Array.isArray) as unknown[][];
  const groups = arrays.find((items) => items.every(isGroup)) as UniBoxGroup[] | undefined;
  const entities = arrays.find((items) => items.every(isEntity)) as UniBoxEntity[] | undefined;
  return { groups: groups || [], entities: entities || [] };
};

// Status lookups go through the global taxonomy (taxonomy.ts). Sales statuses
// resolve to rich defs (color/dot/pill/icon); other modules' statuses (e.g. AI
// Workers 'new'/'processing') fall back to a neutral label since they declare
// their own vocab in config.
const getStatusDefSafe = (statusKey: string) => {
  const def = getStatusDef(statusKey);
  return def.key === statusKey ? def : null;
};

const getStatusLabel = (_config: UniBoxConfig, statusKey: string) =>
  getStatusDefSafe(statusKey)?.label || formatLabel(statusKey);

// Resolve status visual classes from the taxonomy (with a neutral fallback for
// non-global statuses like AI Workers 'new'/'processing').
const statusStyles = (statusKey: string) => {
  const def = getStatusDefSafe(statusKey);
  return {
    pill: def?.pill || 'border-white/12 bg-white/[0.05] text-gray-200',
    dot: def?.dot || 'bg-white/40',
    text: def?.color || 'text-white/70',
    soft: def ? `bg-current/10` : 'bg-white/[0.05]',
  };
};

const getChannelMeta = (channel: string): ChannelMeta =>
  CHANNEL_META[channel] || { label: formatLabel(channel), icon: 'bx:message-rounded', accent: 'text-gray-200', soft: 'bg-white/[0.06]' };

const formatLabel = (value: string) => value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const formatShortTime = (value: string) =>
  new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(value));

const formatRelativeDay = (value: string) => {
  const date = new Date(value);
  const now = new Date('2026-07-01T12:00:00Z');
  if (date.toDateString() === now.toDateString()) return formatShortTime(value);
  return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(date);
};

// Resolve a lead's IANA timezone from the entity. Real leads carry `timezone`
// (e.g. "Europe/Madrid"); fall back to UTC when absent or invalid so we never
// show a wrong wall-clock time.
const resolveTimezone = (entity: UniBoxEntity): string => {
  const raw = typeof entity.timezone === 'string' ? entity.timezone : '';
  if (!raw) return 'UTC';
  try {
    // Intl throws on invalid tz — accept only if it round-trips.
    Intl.DateTimeFormat('en', { timeZone: raw });
    return raw;
  } catch {
    return 'UTC';
  }
};

const formatWeekday = (value: string) =>
  new Intl.DateTimeFormat('en', { weekday: 'short' }).format(new Date(value));

const formatDayMonth = (value: string) =>
  new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(new Date(value)).toUpperCase();

// Friendly "Mon, Jul 3" used on Kanban cards — weekday name + concise date.
const formatWeekdayDate = (value: string) =>
  new Intl.DateTimeFormat('en', { weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(value));

// Fallback currency when neither the lead nor the team specifies one. The team
// currency from Team preferences overrides this at render time (passed as prop).
const CURRENCY_FALLBACK = 'EUR';

// Format an AI-estimated opportunity value. Currency resolution: lead override
// → team currency (prop) → EUR fallback. Invalid codes fall back gracefully.
const formatOpportunityValue = (value: number, currency?: string | null) => {
  const code = (currency || CURRENCY_FALLBACK).toUpperCase();
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(value);
  } catch {
    return new Intl.NumberFormat('en', { style: 'currency', currency: CURRENCY_FALLBACK, maximumFractionDigits: 0 }).format(value);
  }
};

// Wall-clock time of the send IN THE LEAD'S LOCAL TIMEZONE (not the sender's),
// e.g. "10:15" with the tz abbreviation. Outreach is global, so this is what
// the lead actually experiences.
const formatLocalSendTime = (value: string, timezone: string) => {
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    }).formatToParts(new Date(value));
    const time = parts.find((p) => p.type === 'hour')?.value || '';
    const minute = parts.find((p) => p.type === 'minute')?.value || '';
    const tz = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    return `${time}:${minute}${tz ? ` ${tz}` : ''}`;
  } catch {
    return formatShortTime(value);
  }
};

const formatMonthLabel = (date: Date) => new Intl.DateTimeFormat('en', { month: 'long', year: 'numeric' }).format(date);

// The ISO week (Mon-Sun) a date falls in, as a sortable key + a readable label.
// Used by the "group by week" board dimension.
const weekBucket = (value: string): { key: string; label: string } => {
  const d = new Date(value);
  const day = (d.getDay() + 6) % 7; // Mon=0
  const monday = new Date(d);
  monday.setDate(d.getDate() - day);
  monday.setHours(0, 0, 0, 0);
  const key = monday.toISOString().slice(0, 10);
  const fmt = (x: Date) => new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(x);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const prefix = monday.toDateString() === new Date(TODAY.toDateString()).toDateString() ? 'This week · ' : '';
  return { key, label: `${prefix}${fmt(monday)} – ${fmt(sunday)}` };
};

// Used by the "group by day" board dimension — one bucket per calendar day.
const dayBucket = (value: string): { key: string; label: string } => {
  const d = new Date(value);
  const key = d.toISOString().slice(0, 10);
  const prefix = d.toDateString() === TODAY.toDateString() ? 'Today · ' : '';
  const label = `${prefix}${new Intl.DateTimeFormat('en', { weekday: 'short', month: 'short', day: 'numeric' }).format(d)}`;
  return { key, label };
};

const OTHER_SUBSTATUS_KEY = '__other';

type KanbanGroupBucket = {
  key: string;
  label: string;
  items: UniBoxEntity[];
  icon?: string;
  dotClassName?: string;
  toneClassName?: string;
  colorIndex?: number;
};

type UniBoxPersistedViewPreferences = {
  version: number;
  selectedGroupIds: string[];
  selectedStatuses: string[];
  selectedChannels: string[];
  groupBy: GroupByKey;
  leadCardDensity: KanbanCardDensity;
  focusedGroupId: string | null;
};

const UNIBOX_VIEW_PREFS_VERSION = 1;
const GROUP_BY_KEYS: ReadonlySet<string> = new Set(['campaign', 'week', 'day', 'channel', 'substatus']);
const KANBAN_CARD_DENSITY_VALUES: ReadonlySet<string> = new Set(['detail', 'compact']);

const toStorageKeyPart = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'default';

const getUniBoxViewPrefsStorageKey = (product: string) =>
  `brainiacs.unibox.${toStorageKeyPart(product)}.view-prefs.v${UNIBOX_VIEW_PREFS_VERSION}`;

const readStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const readUniBoxViewPreferences = (storageKey: string): Partial<UniBoxPersistedViewPreferences> | null => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<UniBoxPersistedViewPreferences>;
    return {
      selectedGroupIds: readStringArray(parsed.selectedGroupIds),
      selectedStatuses: readStringArray(parsed.selectedStatuses),
      selectedChannels: readStringArray(parsed.selectedChannels),
      groupBy: typeof parsed.groupBy === 'string' && GROUP_BY_KEYS.has(parsed.groupBy) ? parsed.groupBy as GroupByKey : undefined,
      leadCardDensity: typeof parsed.leadCardDensity === 'string' && KANBAN_CARD_DENSITY_VALUES.has(parsed.leadCardDensity) ? parsed.leadCardDensity as KanbanCardDensity : undefined,
      focusedGroupId: typeof parsed.focusedGroupId === 'string' ? parsed.focusedGroupId : null,
    };
  } catch {
    return null;
  }
};

const writeUniBoxViewPreferences = (storageKey: string, preferences: UniBoxPersistedViewPreferences) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(preferences));
  } catch {
    // Preferences are a convenience layer; failing to write should never break the board.
  }
};

const pruneStringSet = (current: Set<string>, allowed: Set<string>) => {
  let changed = false;
  const next = new Set<string>();
  current.forEach((value) => {
    if (allowed.has(value)) next.add(value);
    else changed = true;
  });
  return changed ? next : current;
};

const kanbanCardDateMs = (entity: UniBoxEntity) =>
  new Date(entity.next_send?.scheduled_at || entity.last_activity_at).getTime();

const sortKanbanCardsByDate = (items: UniBoxEntity[]) =>
  [...items].sort((a, b) => {
    const byDate = kanbanCardDateMs(a) - kanbanCardDateMs(b);
    if (byDate !== 0) return byDate;
    return a.client_name.localeCompare(b.client_name);
  });

/**
 * Sub-bucket a column's leads by the active group-by dimension. Each bucket
 * has a stable key + a readable label (rendered as a sub-header above its
 * cards). Date dimensions use next_send or last_activity as the reference.
 * Returns a single "All" bucket when grouping adds no value.
 */
const bucketColumnItems = (
  items: UniBoxEntity[],
  groupBy: GroupByKey,
  config: UniBoxConfig,
  groups: UniBoxGroup[],
): KanbanGroupBucket[] => {
  if (groupBy === 'campaign') {
    // Group by campaign. In All-campaigns mode this surfaces per-campaign
    // sub-sections; when a single campaign is focused it collapses to one.
    const map = new Map<string, UniBoxEntity[]>();
    items.forEach((e) => {
      const gk = String(e[config.group_by.key] || '') || 'ungrouped';
      if (!map.has(gk)) map.set(gk, []);
      map.get(gk)!.push(e);
    });
    return Array.from(map.entries()).map(([gk, list]) => {
      const name = groups.find((g) => g.id === gk)?.name || gk;
      const colorIndex = getGroupColorIndex(groups, gk);
      return {
        key: `cmp:${gk}`,
        label: name,
        items: list,
        icon: config.group_by.label.toLowerCase() === 'campaign' ? 'bx:briefcase-alt-2' : 'bx:collection',
        toneClassName: 'text-white/78',
        colorIndex,
      };
    });
  }
  if (groupBy === 'channel') {
    const map = new Map<string, UniBoxEntity[]>();
    items.forEach((e) => {
      const primary = e.channels[0] || 'other';
      if (!map.has(primary)) map.set(primary, []);
      map.get(primary)!.push(e);
    });
    return Array.from(map.entries()).map(([ch, list]) => ({
      key: `ch:${ch}`,
      label: getChannelMeta(ch).label,
      items: list,
      icon: getChannelMeta(ch).icon,
      toneClassName: 'text-white/78',
    }));
  }
  if (groupBy === 'substatus') {
    const map = new Map<string, { label: string; items: UniBoxEntity[]; icon?: string; toneClassName?: string }>();
    items.forEach((entity) => {
      const statusKey = entity.status;
      const substatus = typeof entity.substatus === 'string' ? entity.substatus.trim() : '';
      const outcome = typeof entity.outcome === 'string' ? entity.outcome.trim() : '';
      const key = substatus || (statusKey === 'discarded' && outcome ? outcome : OTHER_SUBSTATUS_KEY);
      const def = config.substatusByStage[statusKey]?.find((item) => item.key === key);
      const statusDef = getStatusDefSafe(statusKey);
      const entry = map.get(`${statusKey}:${key}`) || {
        label: def?.label || (key === OTHER_SUBSTATUS_KEY ? 'Other' : formatLabel(key)),
        items: [],
        icon: def?.icon || statusDef?.icon,
        toneClassName: 'text-white/78',
      };
      entry.items.push(entity);
      map.set(`${statusKey}:${key}`, entry);
    });
    return Array.from(map.entries()).map(([key, entry]) => ({
      key: `sub:${key}`,
      label: entry.label,
      items: entry.items,
      icon: entry.icon || 'bx:tag',
      toneClassName: entry.toneClassName,
    }));
  }
  // Date dimensions — order chronologically (oldest first).
  const bucketFn = groupBy === 'week' ? weekBucket : dayBucket;
  const map = new Map<string, { label: string; items: UniBoxEntity[] }>();
  items.forEach((e) => {
    const ref = e.next_send?.scheduled_at || e.last_activity_at;
    const b = bucketFn(ref);
    if (!map.has(b.key)) map.set(b.key, { label: b.label, items: [] });
    map.get(b.key)!.items.push(e);
  });
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([key, entry]) => ({
    key,
    label: entry.label,
    items: entry.items,
    icon: groupBy === 'week' ? 'bx:calendar-week' : 'bx:calendar',
    toneClassName: 'text-white/78',
  }));
};

const getInitials = (name: string) =>
  name.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase();

const useUniBoxFeed = (api: UniBoxApi) => {
  const [feed, setFeed] = useState<UniBoxFeed | null>(null);
  useEffect(() => {
    let active = true;
    setFeed(null);
    api.getFeed().then((nextFeed) => { if (active) setFeed(nextFeed); });
    return () => { active = false; };
  }, [api]);
  return feed;
};

// Fixed "today" used across the dummy dataset so relative labels stay stable.
const TODAY = new Date('2026-07-01T12:00:00Z');
const isSameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

// Smart ISR-workspace tab by lead status: outreaching → Sequence;
// otherwise Briefing.
const defaultISRTabFor = (status: string): 'briefing' | 'guidance' | 'sequence' | 'campaign' | 'psych' | 'network' | 'signals' | 'opportunities' | 'news' => {
  if (status === 'outreaching') return 'sequence';
  return 'briefing';
};

const READ_ONLY_ISR_ACTOR = { name: 'Sales Intelligence preview' } as const;
const ignoreReadOnlyISRSave = () => undefined;

type UniBoxMessage = UniBoxThread['messages'][number];
type InteractionStatus = NonNullable<Interaction['meta']>['status'];

const channelToInteractionType = (channel: string): Interaction['type'] => {
  if (channel === 'email' || channel === 'call' || channel === 'linkedin' || channel === 'whatsapp' || channel === 'sms') return channel;
  return 'note';
};

const messageStatusToInteractionStatus = (state: UniBoxMessage['state']): InteractionStatus => {
  if (state === 'received') return 'read';
  if (state === 'scheduled') return 'scheduled';
  return 'sent';
};

const firstValidTimestamp = (...values: Array<string | null | undefined>) =>
  values.find((value) => Boolean(value) && !Number.isNaN(new Date(value as string).getTime())) || TODAY.toISOString();

const sortInteractions = (items: Interaction[]) =>
  [...items].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

const buildLeadInteractions = (entity: UniBoxEntity, threads: UniBoxThread[]): Interaction[] => {
  const seen = new Set<string>();
  const items: Interaction[] = [];
  const add = (interaction: Interaction) => {
    const key = `${interaction.timestamp}:${interaction.type}:${interaction.content}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push(interaction);
  };

  (entity.sequenceSteps || []).forEach((step) => {
    if (!step.sentAt) return;
    add({
      id: `${entity.lvl1_id}-${step.channel}-${step.step}-sent`,
      type: channelToInteractionType(step.channel),
      direction: 'outbound',
      participantName: 'Brainiacs AI',
      participantRole: 'agent',
      avatar: null,
      timestamp: firstValidTimestamp(step.sentAt, entity.last_activity_at),
      content: step.body,
      meta: {
        subject: step.subject || `Message ${step.step}`,
        status: 'sent',
      },
    });
  });

  threads.forEach((thread) => {
    thread.messages.forEach((message, index) => {
      const outbound = message.direction === 'outbound';
      const timestamp = firstValidTimestamp(message.sent_at, message.received_at, message.scheduled_at, entity.last_activity_at);
      add({
        id: `${entity.lvl1_id}-${thread.channel}-${message.id ?? `local-${index}`}`,
        type: channelToInteractionType(thread.channel),
        direction: message.direction,
        participantName: outbound ? (message.sent_by === 'human' ? 'You' : 'Brainiacs AI') : entity.client_name,
        participantRole: outbound ? 'agent' : 'lead',
        avatar: outbound ? null : entity.avatar,
        timestamp,
        content: message.body,
        meta: {
          subject: message.sequence_step ? `Message ${message.sequence_step}` : undefined,
          status: messageStatusToInteractionStatus(message.state),
        },
      });
    });
  });

  if (entity.next_send) {
    add({
      id: `${entity.lvl1_id}-${entity.next_send.channel}-${entity.next_send.sequence_step}-scheduled`,
      type: channelToInteractionType(entity.next_send.channel),
      direction: 'outbound',
      participantName: 'Brainiacs AI',
      participantRole: 'agent',
      avatar: null,
      timestamp: firstValidTimestamp(entity.next_send.scheduled_at, entity.last_activity_at),
      content: entity.next_send.preview,
      meta: {
        subject: `Message ${entity.next_send.sequence_step}`,
        status: 'scheduled',
      },
    });
  }

  if (items.length === 0 && entity.last_message_snippet) {
    const inbound = entity.needs_reply;
    add({
      id: `${entity.lvl1_id}-last-activity`,
      type: channelToInteractionType(entity.channels[0] || 'note'),
      direction: inbound ? 'inbound' : 'outbound',
      participantName: inbound ? entity.client_name : 'Brainiacs AI',
      participantRole: inbound ? 'lead' : 'agent',
      avatar: inbound ? entity.avatar : null,
      timestamp: firstValidTimestamp(entity.last_activity_at),
      content: entity.last_message_snippet,
      meta: { status: inbound ? 'read' : 'sent' },
    });
  }

  return sortInteractions(items);
};

const mapUniBoxLead = (entity: UniBoxEntity, threads: UniBoxThread[]): MappedLeadData => ({
  ...uniBoxEntityToMappedLead(entity),
  interactions: buildLeadInteractions(entity, threads),
});

export const UniBox: React.FC<UniBoxProps> = ({ config, api, campaigns, lens: lensProp, view: viewProp, mailbox: mailboxProp, calRange: calRangeProp, onLensChange, onViewChange, onMailboxChange, onCalRangeChange, onUniBoxChange, onOpenLead, onOpenCampaignDetails, onSaveSalesState, currency, toolbarSlot }) => {
  const feed = useUniBoxFeed(api);
  const { groups, entities } = useMemo(() => getFeedCollections(feed || {}), [feed]);
  const availableViewModes = config.view_modes?.length ? config.view_modes : (['inbox', 'kanban'] as UniBoxViewMode[]);
  const viewPrefsStorageKey = useMemo(() => getUniBoxViewPrefsStorageKey(config.product), [config.product]);
  const persistedViewPreferences = useMemo(() => readUniBoxViewPreferences(viewPrefsStorageKey), [viewPrefsStorageKey]);

  // --- Multi-select filter state. Campaigns & channels are always filters;
  //     Kanban columns are fixed to Status, Calendar color is fixed to Campaign. ---
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(() => new Set(persistedViewPreferences?.selectedGroupIds || []));
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(() => new Set(persistedViewPreferences?.selectedStatuses || []));
  const [selectedChannels, setSelectedChannels] = useState<Set<string>>(() => new Set(persistedViewPreferences?.selectedChannels || []));
  const [searchTerm, setSearchTerm] = useState('');
  // Group-by dimension for the full-bleed boards (Campaign / Week / Day / Channel / Substatus).
  const [groupBy, setGroupBy] = useState<GroupByKey>(() => persistedViewPreferences?.groupBy || 'campaign');
  const [leadCardDensity, setLeadCardDensity] = useState<KanbanCardDensity>(() => persistedViewPreferences?.leadCardDensity || 'detail');
  // Pipeline Kanban campaign focus: null = All campaigns, else isolate one.
  const [focusedGroupId, setFocusedGroupId] = useState<string | null>(() => persistedViewPreferences?.focusedGroupId ?? null);
  // Isolates the items that need the human (escalated + needs_reply) — the
  // "do the action and clean it" triage workflow.
  const [actionOnly, setActionOnly] = useState(false);
  // Outreach substatus fast-filter (stage within the outreach sequence).
  const [selectedSubstatus, setSelectedSubstatus] = useState<Set<string>>(new Set());
  const toggleSubstatus = useCallback((key: string) => {
    setSelectedSubstatus((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const validPersistedStatuses = useMemo(() => {
    const values = new Set(config.statuses);
    if (config.statuses.includes('leads') && config.statuses.includes('enriched')) values.add('lead_pool');
    return values;
  }, [config.statuses]);
  const validPersistedChannels = useMemo(() => new Set(config.channels), [config.channels]);
  const validPersistedGroupIds = useMemo(() => new Set(groups.map((group) => group.id)), [groups]);

  useEffect(() => {
    setSelectedStatuses((current) => pruneStringSet(current, validPersistedStatuses));
  }, [validPersistedStatuses]);

  useEffect(() => {
    setSelectedChannels((current) => pruneStringSet(current, validPersistedChannels));
  }, [validPersistedChannels]);

  useEffect(() => {
    if (groups.length === 0) return;
    setSelectedGroupIds((current) => pruneStringSet(current, validPersistedGroupIds));
    setFocusedGroupId((current) => (current && !validPersistedGroupIds.has(current) ? null : current));
  }, [groups.length, validPersistedGroupIds]);

  useEffect(() => {
    writeUniBoxViewPreferences(viewPrefsStorageKey, {
      version: UNIBOX_VIEW_PREFS_VERSION,
      selectedGroupIds: Array.from(selectedGroupIds).sort(),
      selectedStatuses: Array.from(selectedStatuses).sort(),
      selectedChannels: Array.from(selectedChannels).sort(),
      groupBy,
      leadCardDensity,
      focusedGroupId,
    });
  }, [focusedGroupId, groupBy, leadCardDensity, selectedChannels, selectedGroupIds, selectedStatuses, viewPrefsStorageKey]);

  // Lens = primary nav (which part of the funnel). URL-controlled when provided.
  const [lensLocal, setLensLocal] = useState<UniBoxLens>('pipeline');
  const lens = lensProp ?? lensLocal;

  // View state is controlled by URL when a prop is provided; local otherwise.
  const [viewModeLocal, setViewModeLocal] = useState<UniBoxViewMode>(availableViewModes[0] || 'inbox');
  const [mailboxModeLocal, setMailboxModeLocal] = useState<'inbox' | 'queue'>('inbox');
  const [calRangeLocal, setCalRangeLocal] = useState<'month' | 'week'>('week');
  const viewMode = viewProp ?? viewModeLocal;
  const mailboxMode = mailboxProp ?? mailboxModeLocal;
  const calRange = calRangeProp ?? calRangeLocal;

  const [calCursor, setCalCursor] = useState<Date>(new Date(TODAY));
  const [selectedCalDay, setSelectedCalDay] = useState<Date>(new Date(TODAY));

  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [sideDrawerOpen, setSideDrawerOpen] = useState(false);
  // Lead-details modal (Leads Intelligence full profile) opened from the preview.
  const [leadModalEntity, setLeadModalEntity] = useState<UniBoxEntity | null>(null);
  const [previewClosed, setPreviewClosed] = useState(false);
  const [activeChannel, setActiveChannel] = useState<string>('');
  const [controlOverrides, setControlOverrides] = useState<Record<number, UniBoxControlState>>({});
  const [threadCache, setThreadCache] = useState<Record<string, UniBoxThread>>({});
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionsKey, setSuggestionsKey] = useState<string>('');
  const [composerValue, setComposerValue] = useState('');

  // The signature GSAP fx. The stage remounts on viewStageKey change and the
  // reveal re-fires. Every view/sub-view switch goes through requestViewChange
  // so the blur-out runs before the swap and the blur-in runs after.
  // NOTE: `lens` is part of the key so lens-only changes (e.g. Inbox ↔ All,
  // which share the same viewMode/mailbox/calRange) still remount the stage and
  // re-fire the in-fx. Without it the out-fx leaves blocks invisible.
  const viewStageKey = `${lens}:${viewMode}:${mailboxMode}:${calRange}`;
  const stageRef = useStageReveal(viewStageKey);
  // Guard against overlapping transitions: if a click lands while a blur-out is
  // mid-flight, kill it and run the new swap immediately. This was the root
  // cause of "click 2-3 times before the tab changes" — repeated clicks queued
  // multiple swaps, and the lens prop (URL-controlled) lagged the GSAP timer.
  const activeTweenRef = useRef<gsap.core.Tween | null>(null);
  const transitionOutThen = useCallback((swap: () => void) => {
    const targets = collectViewBlocks(stageRef.current);
    if (activeTweenRef.current) {
      activeTweenRef.current.kill();
      activeTweenRef.current = null;
    }
    if (!targets.length) { swap(); return; }
    // Reset any inline styles left by a previous reveal so the out-fx starts
    // from a clean, fully-visible state (prevents a flash of invisible blocks).
    gsap.set(targets, { clearProps: 'opacity,y,filter' });
    activeTweenRef.current = gsap.to(targets, {
      opacity: 0, y: -10, filter: 'blur(12px)', duration: 0.26, stagger: 0.018, ease: 'power2.in',
      onComplete: () => { activeTweenRef.current = null; swap(); },
    });
  }, [stageRef]);

  const requestViewChange = useCallback((swap: () => void) => {
    transitionOutThen(swap);
  }, [transitionOutThen]);

  // Apply a view-state patch. When `onUniBoxChange` is wired (URL-controlled),
  // emit ONE batched navigate so multi-field actions (e.g. a lens switch that
  // also seeds a default view + mailbox) hit the URL atomically — the previous
  // per-field approach fired 2-3 navigates that each closed over STALE search
  // params and clobbered each other, so the lens change silently reverted and
  // users had to click a tab multiple times. When no batched handler is set,
  // fall back to the per-field callbacks (each its own write) or local state.
  const applyViewPatch = useCallback((patch: UniBoxViewPatch) => {
    if (onUniBoxChange) {
      onUniBoxChange(patch);
      return;
    }
    if (patch.lens && onLensChange) onLensChange(patch.lens);
    else if (patch.lens) setLensLocal(patch.lens);
    if (patch.view && onViewChange) onViewChange(patch.view);
    else if (patch.view) setViewModeLocal(patch.view);
    if (patch.mailbox && onMailboxChange) onMailboxChange(patch.mailbox);
    else if (patch.mailbox) setMailboxModeLocal(patch.mailbox);
    if (patch.calRange && onCalRangeChange) onCalRangeChange(patch.calRange);
    else if (patch.calRange) setCalRangeLocal(patch.calRange);
  }, [onCalRangeChange, onLensChange, onMailboxChange, onUniBoxChange, onViewChange]);

  const setView = useCallback((next: UniBoxViewMode) => {
    if (next === viewMode) return;
    requestViewChange(() => {
      applyViewPatch({ view: next });
      setSideDrawerOpen(false);
    });
  }, [applyViewPatch, requestViewChange, viewMode]);

  const setMailbox = useCallback((next: 'inbox' | 'queue') => {
    if (next === mailboxMode) return;
    requestViewChange(() => {
      applyViewPatch({ mailbox: next });
    });
  }, [applyViewPatch, mailboxMode, requestViewChange]);

  const setRange = useCallback((next: 'month' | 'week') => {
    if (next === calRange) return;
    requestViewChange(() => {
      applyViewPatch({ calRange: next });
    });
  }, [applyViewPatch, calRange, requestViewChange]);

  // Lens metadata: label, icon, the default view when you enter it, and a
  // short description for the title row. goLens switches lens + seeds a
  // sensible default view for that lens.
  const LENS_META: Record<UniBoxLens, { label: string; icon: string; defaultView: UniBoxViewMode; hint: string }> = {
    pipeline: { label: 'Mission Control', icon: 'bx:columns', defaultView: 'kanban', hint: 'Full funnel — where every lead sits' },
    outreach: { label: 'Outreach', icon: 'bx:paper-plane', defaultView: 'inbox', hint: 'The send plan — what fires and when' },
    inbox: { label: 'Inbox', icon: 'bx:inbox', defaultView: 'inbox', hint: 'Conversations that need you' },
    all: { label: 'All leads', icon: 'bx:grid-alt', defaultView: 'inbox', hint: 'Everyone — search & overview' },
  };

  const goLens = useCallback((next: UniBoxLens) => {
    if (next === lens) return;
    const meta = LENS_META[next];
    requestViewChange(() => {
      // Build the FULL next state and emit it as ONE patch so a lens switch
      // (which also re-seeds view + mailbox) lands in a single URL write.
      const patch: UniBoxViewPatch = { lens: next };
      if (meta.defaultView !== viewMode) patch.view = meta.defaultView;
      if (next === 'outreach' && mailboxMode !== 'queue') patch.mailbox = 'queue';
      applyViewPatch(patch);
      setSideDrawerOpen(false);
    });
  }, [LENS_META, lens, mailboxMode, applyViewPatch, requestViewChange, viewMode]);

  // Corrective effects: coerce an invalid view/mailbox back to a valid combo.
  // Routed through applyViewPatch so that, when URL-controlled, each correction
  // is a SINGLE navigate (with the field it owns) rather than a competing
  // per-field write that closes over stale search params.
  useEffect(() => {
    if (!availableViewModes.includes(viewMode)) {
      const fallback = availableViewModes[0] || 'inbox';
      applyViewPatch({ view: fallback });
    }
  }, [applyViewPatch, availableViewModes, viewMode]);
  useEffect(() => {
    if (viewMode !== 'inbox' && mailboxMode !== 'inbox') {
      applyViewPatch({ mailbox: 'inbox' });
    }
  }, [applyViewPatch, mailboxMode, viewMode]);

  const effectiveEntities = useMemo(
    () => entities.map((entity) => ({ ...entity, control_state: controlOverrides[entity.lvl1_id] || entity.control_state })),
    [controlOverrides, entities],
  );

  const sortedEntities = useMemo(
    () =>
      [...effectiveEntities].sort((a, b) => {
        // Triage priority: AI escalated (needs me) → replied & waiting on me →
        // human-takeover → everything else, then by most recent activity.
        const rank = (e: UniBoxEntity) => {
          if (e.control_state === 'escalated') return 0;
          if (e.needs_reply) return 1;
          if (e.control_state === 'human_takeover') return 2;
          return 3;
        };
        if (rank(a) !== rank(b)) return rank(a) - rank(b);
        return new Date(b.last_activity_at).getTime() - new Date(a.last_activity_at).getTime();
      }),
    [effectiveEntities],
  );

  // Each lens focuses a slice of the funnel. Pipeline/All see everything;
  // Outreach = outreaching + outreached leads (the send plan and what was
  // sent, awaiting replies); Inbox = engaged+ leads (conversations and deals to work).
  // This is the coarse focus — the status pill bar refines WITHIN the lens.
  const lensStatusSlice = useMemo<Set<string> | null>(() => {
    if (lens === 'outreach') return new Set(['outreaching', 'outreached']);
    if (lens === 'inbox') return new Set(['engaged', 'proposal', 'converted']);
    return null; // pipeline + all → no status slice
  }, [lens]);
  const statusSelectionControlsKanbanColumns = lens === 'pipeline' || viewMode === 'kanban';

  const filteredEntities = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return sortedEntities.filter((entity) => {
      if (lensStatusSlice && !lensStatusSlice.has(entity.status)) return false;
      const groupValue = String(entity[config.group_by.key] || '');
      const matchesGroup = selectedGroupIds.size === 0 || selectedGroupIds.has(groupValue);
      // The filter exposes a fused "Lead Pool" option (value `lead_pool`) that
      // covers both the `leads` and `enriched` statuses — match either here.
      const poolMatch = (entity.status === 'leads' || entity.status === 'enriched') && selectedStatuses.has('lead_pool');
      const matchesStatus = statusSelectionControlsKanbanColumns || selectedStatuses.size === 0 || selectedStatuses.has(entity.status) || poolMatch;
      const matchesChannel = selectedChannels.size === 0 || entity.channels.some((c) => selectedChannels.has(c));
      const searchable = `${entity.client_name} ${entity.company || ''} ${entity.last_message_snippet} ${entity.next_send?.preview || ''}`.toLowerCase();
      const matchesSearch = !normalizedSearch || searchable.includes(normalizedSearch);
      return matchesGroup && matchesStatus && matchesChannel && matchesSearch;
    });
  }, [config.group_by.key, lensStatusSlice, searchTerm, selectedChannels, selectedGroupIds, selectedStatuses, sortedEntities, statusSelectionControlsKanbanColumns]);

  const queuedEntities = useMemo(
    () =>
      filteredEntities
        .filter((entity) => Boolean(entity.next_send) && (selectedChannels.size === 0 || selectedChannels.has(entity.next_send!.channel)))
        .sort((a, b) => new Date(a.next_send!.scheduled_at).getTime() - new Date(b.next_send!.scheduled_at).getTime()),
    [filteredEntities, selectedChannels],
  );

  const selectionEntities = useMemo(() => {
    if (viewMode === 'calendar') return queuedEntities;
    if (viewMode === 'inbox' && mailboxMode === 'queue') return queuedEntities;
    return filteredEntities;
  }, [filteredEntities, mailboxMode, queuedEntities, viewMode]);

  useEffect(() => {
    if (selectionEntities.length === 0) { setSelectedEntityId(null); return; }
    if (!selectedEntityId || !selectionEntities.some((entity) => entity.lvl1_id === selectedEntityId)) {
      setSelectedEntityId(selectionEntities[0].lvl1_id);
    }
  }, [selectedEntityId, selectionEntities]);

  const selectedEntity = useMemo(
    () => effectiveEntities.find((entity) => entity.lvl1_id === selectedEntityId) || null,
    [effectiveEntities, selectedEntityId],
  );

  useEffect(() => {
    if (!selectedEntity) { setActiveChannel(''); return; }
    setActiveChannel((currentChannel) => (currentChannel && selectedEntity.channels.includes(currentChannel) ? currentChannel : selectedEntity.channels[0] || ''));
    setSuggestions([]);
    setComposerValue('');
  }, [selectedEntity?.lvl1_id]);

  const threadKey = selectedEntity && activeChannel ? `${selectedEntity.lvl1_id}_${activeChannel}` : '';
  const selectedChannelSignature = selectedEntity?.channels.join('|') || '';

  useEffect(() => {
    if (!selectedEntity) return;
    const missingChannels = selectedEntity.channels.filter((channel) => !threadCache[`${selectedEntity.lvl1_id}_${channel}`]);
    if (!missingChannels.length) return;
    let active = true;
    Promise.all(
      missingChannels.map((channel) =>
        api.getThread(selectedEntity.lvl1_id, channel).then((thread) => [`${selectedEntity.lvl1_id}_${channel}`, thread] as const),
      ),
    ).then((entries) => {
      if (!active) return;
      setThreadCache((current) => {
        let changed = false;
        const next = { ...current };
        entries.forEach(([key, thread]) => {
          if (next[key]) return;
          next[key] = thread;
          changed = true;
        });
        return changed ? next : current;
      });
    });
    return () => { active = false; };
  }, [api, selectedChannelSignature, selectedEntity, threadCache]);

  const selectedControl = selectedEntity?.control_state || 'ai_running';
  const composerLocked = selectedControl === 'ai_running';
  const activeChannelMeta = getChannelMeta(activeChannel);

  const setConversationControl = useCallback(
    (nextState: UniBoxControlState) => {
      if (!selectedEntity || !threadKey) return;
      setControlOverrides((current) => ({ ...current, [selectedEntity.lvl1_id]: nextState }));
      setThreadCache((current) => {
        const currentThread = current[threadKey] || { lvl1_id: selectedEntity.lvl1_id, channel: activeChannel, messages: [] };
        return { ...current, [threadKey]: { ...currentThread, control_state: nextState } };
      });
    },
    [activeChannel, selectedEntity, threadKey],
  );

  const handleLoadSuggestions = useCallback(() => {
    if (!selectedEntity || !activeChannel) return;
    const nextKey = `${selectedEntity.lvl1_id}_${activeChannel}`;
    api.getSuggestions(selectedEntity.lvl1_id, activeChannel).then((nextSuggestions) => { setSuggestions(nextSuggestions); setSuggestionsKey(nextKey); });
  }, [activeChannel, api, selectedEntity]);

  const handleSend = useCallback(() => {
    if (!selectedEntity || !activeChannel || !threadKey || !composerValue.trim() || composerLocked) return;
    const message = { id: `local-${Date.now()}`, direction: 'outbound' as const, state: 'sent' as const, sent_by: 'human' as const, body: composerValue.trim(), sent_at: new Date().toISOString() };
    setThreadCache((current) => {
      const currentThread = current[threadKey] || { lvl1_id: selectedEntity.lvl1_id, channel: activeChannel, messages: [] };
      return { ...current, [threadKey]: { ...currentThread, control_state: 'human_takeover', messages: [...currentThread.messages, message] } };
    });
    setControlOverrides((current) => ({ ...current, [selectedEntity.lvl1_id]: 'human_takeover' }));
    setComposerValue('');
  }, [activeChannel, composerLocked, composerValue, selectedEntity, threadKey]);

  const channelCounts = useMemo(
    () => config.channels.reduce<Record<string, number>>((acc, channel) => {
      acc[channel] = effectiveEntities.filter((entity) => entity.channels.includes(channel)).length;
      return acc;
    }, {}),
    [config.channels, effectiveEntities],
  );

  const needsHumanTotal = effectiveEntities.filter((entity) => entity.control_state === 'escalated').length;
  const needsReplyTotal = filteredEntities.filter((entity) => entity.needs_reply).length;
  // Items that need the human right now — the triage surface for "Action needed".
  const actionEntities = useMemo(
    () => filteredEntities.filter((entity) => entity.control_state === 'escalated' || entity.needs_reply),
    [filteredEntities],
  );
  const inboxEntities = actionOnly ? actionEntities : filteredEntities;

  // Calendar load summary: how many sends fire today, this week, and in total.
  // This is the quantified readout the graph alone can't give — it answers
  // "is anything imminent?" at a glance.
  const queueLoad = useMemo(() => {
    const now = TODAY;
    const weekEnd = new Date(now);
    weekEnd.setDate(weekEnd.getDate() + 7);
    let today = 0;
    let week = 0;
    queuedEntities.forEach((entity) => {
      if (!entity.next_send) return;
      const t = new Date(entity.next_send.scheduled_at).getTime();
      if (isSameDay(new Date(t), now)) today += 1;
      if (t >= now.getTime() && t <= weekEnd.getTime()) week += 1;
    });
    return { today, week, total: queuedEntities.length };
  }, [queuedEntities]);
  const suggestionVisible = selectedEntity && suggestionsKey === threadKey && suggestions.length > 0;
  const canUseSuggestions = selectedControl !== 'ai_running';


  // Open the lead's full profile. If the host provides onOpenLead (real wiring),
  // delegate; otherwise open the in-page LeadReportModal built from the entity.
  const openLeadDetails = useCallback((entity: UniBoxEntity) => {
    if (onOpenLead) onOpenLead(entity);
    else setLeadModalEntity(entity);
  }, [onOpenLead]);

  const handleSelectEntity = useCallback((entity: UniBoxEntity, fromQueue: boolean) => {
    // ISR ("Human") surfaces require an enriched lead. Non-enriched entities
    // (new / unprocessed leads) open the plain lead profile instead — the
    // ISR workspace has no briefing, sequence or activity for them yet.
    if (!isLeadEnrichedForISR(entity.status)) {
      openLeadDetails(entity);
      return;
    }

    setSelectedEntityId(entity.lvl1_id);
    setPreviewClosed(false);
    setActiveChannel((fromQueue ? entity.next_send?.channel : entity.channels[0]) || entity.channels[0] || '');
    // The side drawer is the preview surface for calendar AND kanban. The
    // pipeline lens renders kanban regardless of the raw viewMode, so derive
    // "are we on a board view?" from lens + viewMode together (effectiveView is
    // computed lower down; this mirrors its logic without reordering).
    const onBoard = lens === 'pipeline' || viewMode === 'kanban' || viewMode === 'calendar';
    if (onBoard) setSideDrawerOpen(true);
  }, [lens, viewMode, openLeadDetails]);

  // Filter option lists for both the sidebar and the FilterBar.
  const groupFilterOptions = useMemo(
    () => groups.map((group) => ({ value: group.id, label: group.name, count: group.total, dot: GROUP_DOT[getGroupColorIndex(groups, group.id) % GROUP_DOT.length] })),
    [groups],
  );
  const statusFilterOptions = useMemo(() => {
    const baseOption = (key: string) => ({
      value: key,
      label: getStatusDefSafe(key)?.label || formatLabel(key),
      count: effectiveEntities.filter((e) => e.status === key).length,
    });
    // The Kanban fuses `leads` + `enriched` into a single "Lead Pool" column,
    // so the Status filter exposes them as ONE fused option (Lead Pool) at the
    // position of the first of the two, instead of two separate entries.
    if (config.statuses.includes('leads') && config.statuses.includes('enriched')) {
      const poolCount = effectiveEntities.filter((e) => e.status === 'leads' || e.status === 'enriched').length;
      const poolInserted = { value: 'lead_pool', label: 'Lead Pool', count: poolCount };
      const remaining = config.statuses.filter((k) => k !== 'leads' && k !== 'enriched').map(baseOption);
      return [poolInserted, ...remaining];
    }
    return config.statuses.map(baseOption);
  }, [config.statuses, effectiveEntities]);
  const channelFilterOptions = useMemo(
    () => config.channels.map((channel) => ({ value: channel, label: getChannelMeta(channel).label, count: channelCounts[channel] || 0 })),
    [config.channels, channelCounts],
  );

  const activeFilterCount = selectedGroupIds.size + selectedStatuses.size + selectedChannels.size;
  const resetFilters = useCallback(() => {
    setSelectedGroupIds(new Set());
    setSelectedStatuses(new Set());
    setSelectedChannels(new Set());
    setSearchTerm('');
    setFocusedGroupId(null);
  }, []);

  // Pipeline uses the reusable ISR call workspace. This Sales Intelligence
  // surface remains read-only until its feed is backed by canonical lead data;
  // the current entity can be dummy/mock and `lvl1_id` is not a mutation id.


  const groupLabelLower = config.group_by.label;
  const selectedLeadThreads = useMemo(
    () => (selectedEntity ? selectedEntity.channels.map((channel) => threadCache[`${selectedEntity.lvl1_id}_${channel}`]).filter((thread): thread is UniBoxThread => Boolean(thread)) : []),
    [selectedEntity, threadCache],
  );
  const selectedMappedLead = useMemo(
    () => (selectedEntity ? mapUniBoxLead(selectedEntity, selectedLeadThreads) : null),
    [selectedEntity, selectedLeadThreads],
  );
  const leadModalThreads = useMemo(
    () => (leadModalEntity ? Object.values(threadCache).filter((thread) => thread.lvl1_id === leadModalEntity.lvl1_id) : []),
    [leadModalEntity, threadCache],
  );
  const leadModalData = useMemo(
    () => (leadModalEntity ? mapUniBoxLead(leadModalEntity, leadModalThreads) : null),
    [leadModalEntity, leadModalThreads],
  );

  const selectedCampaignId = selectedEntity ? String(selectedEntity.campaign_id || '') : '';
  const selectedCampaign = useMemo(() => {
    if (!selectedCampaignId || !campaigns?.length) return undefined;
    return campaigns.find((campaign) => String(campaign.id) === selectedCampaignId);
  }, [campaigns, selectedCampaignId]);
  const drawerDefaultTab = selectedEntity ? defaultISRTabFor(selectedEntity.status) : 'briefing';
  const openSelectedLeadDetails = useCallback(() => {
    if (selectedEntity) openLeadDetails(selectedEntity);
  }, [openLeadDetails, selectedEntity]);
  const splitDrawerOpen = !previewClosed && !!selectedEntity;

  // The effective surface to render, reconciling lens + viewMode. Pipeline
  // always renders Kanban (it IS the funnel board). Outreach renders calendar
  // when that sub-view is chosen, else the queue (an inbox-list variant).
  // Inbox/All render the inbox split.
  const effectiveView: 'kanban' | 'calendar' | 'inbox' =
    lens === 'pipeline' ? 'kanban' : lens === 'outreach' ? (viewMode === 'calendar' ? 'calendar' : 'inbox') : viewMode === 'kanban' ? 'kanban' : viewMode === 'calendar' ? 'calendar' : 'inbox';
  const useDocumentScroll = config.product === 'sales_intelligence' && effectiveView === 'kanban';
  // Outreach lens always shows the queue (the send plan). Other lenses respect
  // the inbox/queue mailbox toggle.
  const showQueue = lens === 'outreach' ? true : mailboxMode === 'queue';
  // Which preview surface a lead opens into. Pipeline → full ISR Workspace
  // (the lead-context detail surface). Outreach → SequenceBoard inline rail
  // (the send plan). Inbox/All → conversation inline rail (chat/email thread).
  // The full lead profile is still reachable from each inline preview via
  // the "Lead details" affordance (openLeadDetails).
  const previewMode: 'drawer' | 'sequence' | 'conversation' =
    lens === 'outreach' ? 'sequence' : lens === 'inbox' || lens === 'all' ? 'conversation' : 'drawer';
  const usesInlinePreview = previewMode !== 'drawer';
  const leadDrawerOpen = !usesInlinePreview && (effectiveView === 'inbox' ? splitDrawerOpen : sideDrawerOpen && !!selectedEntity);
  const closeLeadDrawer = () => {
    if (effectiveView === 'inbox') setPreviewClosed(true);
    else setSideDrawerOpen(false);
  };
  // The inline preview rail is open whenever a lead is selected and the user
  // hasn't dismissed it. Board views (kanban/calendar) gate on sideDrawerOpen;
  // the inbox split gates on splitDrawerOpen (previewClosed flag).
  const inlinePreviewOpen = effectiveView === 'inbox' ? splitDrawerOpen : sideDrawerOpen && !!selectedEntity;
  const closeInlinePreview = closeLeadDrawer;

  // Conversation preview props for the inline MessagePreviewContent rail. Built
  // once and shared across inbox/calendar/kanban layouts so the preview stays in
  // sync as the selected entity / active channel / thread change. Null when no
  // entity is selected or when the active lens isn't a conversation lens.
  const inlinePreviewContent = useMemo(() => {
    if (!selectedEntity || previewMode !== 'conversation') return null;
    return {
      config,
      entity: selectedEntity,
      activeChannel,
      onActiveChannelChange: setActiveChannel,
      controlState: selectedControl,
      thread: threadKey ? threadCache[threadKey] : undefined,
      channelMeta: getChannelMeta,
      composerValue,
      onComposerChange: setComposerValue,
      onSend: handleSend,
      composerLocked,
      suggestions,
      suggestionVisible: !!suggestionVisible,
      canUseSuggestions,
      onLoadSuggestions: handleLoadSuggestions,
      onApplySuggestion: (value: string) => setComposerValue(value),
      onTakeOver: () => setConversationControl('human_takeover'),
      onReturnToAi: () => setConversationControl('ai_running'),
      onOpenLead: () => openLeadDetails(selectedEntity),
    };
  }, [selectedEntity, previewMode, config, activeChannel, selectedControl, threadKey, threadCache, composerValue, composerLocked, suggestions, suggestionVisible, canUseSuggestions, handleSend, handleLoadSuggestions, setConversationControl, openLeadDetails]);

  // The inline preview rail element — SequenceBoard for Outreach, conversation
  // for Inbox/All. Rendered inside the inbox split AND beside the calendar/kanban
  // boards. Null when the active lens uses the full ISR Workspace (Pipeline).
  const inlinePreviewRail = usesInlinePreview ? (
    <MessagePreviewRail open={inlinePreviewOpen} onClose={closeInlinePreview} content={inlinePreviewContent}>
      {previewMode === 'sequence' && selectedEntity ? (
        <SequenceBoard config={config} entity={selectedEntity} onClose={closeInlinePreview} onOpenLead={() => openLeadDetails(selectedEntity)} />
      ) : null}
    </MessagePreviewRail>
  ) : null;

  const inboxActionCount = needsHumanTotal + needsReplyTotal;
  const lensOptions = (['pipeline', 'outreach', 'inbox', 'all'] as UniBoxLens[]).map((key) => {
    const meta = LENS_META[key];
    const badge = key === 'outreach' ? queuedEntities.length || undefined : key === 'inbox' ? inboxActionCount || undefined : undefined;
    return { value: key, icon: meta.icon, label: meta.label, badge };
  });
  const hasToolbarActions = !!toolbarSlot || lens === 'outreach' || (lens === 'inbox' && inboxActionCount > 0);

  return (
    <div className={`flex w-full flex-col gap-3 ${useDocumentScroll ? 'min-h-[calc(100vh-174px)]' : 'h-[calc(100vh-174px)] min-h-[660px]'}`}>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <div className="inline-flex min-w-0 flex-wrap items-center">
          <SegmentedControl
            ariaLabel="Sales intelligence workspace"
            size="md"
            tone="accent"
            value={lens}
            onChange={(value) => goLens(value as UniBoxLens)}
            options={lensOptions}
          />
        </div>

        {hasToolbarActions && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {lens === 'outreach' && (
              <SegmentedControl
                ariaLabel="Outreach view"
                size="md"
                tone="neutral"
                value={calRange === 'month' || viewMode === 'calendar' ? 'calendar' : 'queue'}
                onChange={(value) => {
                  if (value === 'queue') { setMailbox('queue'); setView('inbox'); }
                  else setView('calendar');
                }}
                options={[
                  { value: 'queue', icon: 'bx:list-ul', label: 'Queue' },
                  { value: 'calendar', icon: 'bx:calendar', label: 'Calendar' },
                ]}
              />
            )}
            {lens === 'inbox' && inboxActionCount > 0 && (
              <button
                type="button"
                onClick={() => setActionOnly((v) => !v)}
                aria-pressed={actionOnly}
                className={`inline-flex h-10 items-center gap-2 rounded-xl px-3 text-[11px] font-black uppercase tracking-[0.12em] transition-all ${
                  actionOnly ? 'bg-white/[0.09] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.055)]' : 'bg-white/[0.025] text-white/50 hover:bg-white/[0.055] hover:text-white/80'
                }`}
              >
                <Icon icon="bx:error-circle" className={`h-4 w-4 ${actionOnly ? 'text-amber-200/80' : 'text-white/38'}`} />
                Action needed
                <span className="rounded-md bg-black/20 px-1.5 py-0.5 text-[10px] text-white/70">{inboxActionCount}</span>
              </button>
            )}
            {toolbarSlot}
          </div>
        )}
      </div>

      <div className={`flex flex-1 gap-4 ${useDocumentScroll ? '' : 'min-h-0'}`}>
        <section className={`flex min-w-0 flex-1 flex-col rounded-[24px] bg-[linear-gradient(180deg,rgba(18,19,24,0.88),rgba(9,10,13,0.96))] shadow-[0_24px_70px_rgba(0,0,0,0.28),inset_0_1px_0_rgba(255,255,255,0.03)] ${useDocumentScroll ? 'overflow-visible' : 'overflow-hidden'}`}>
          {effectiveView === 'calendar' && (
            <div className="shrink-0 px-4 py-3">
              <div className="flex flex-wrap items-center justify-end gap-2" data-view-block>
                <div className="flex items-center gap-1 rounded-xl bg-white/[0.028] p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]">
                  <button type="button" onClick={() => setCalCursor((c) => shiftCursor(c, calRange, -1))} className="flex h-8 w-8 items-center justify-center rounded-lg text-white/50 transition-colors hover:bg-white/[0.06] hover:text-white" aria-label="Previous">
                    <Icon icon="bx:chevron-left" className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => { setCalCursor(new Date(TODAY)); setSelectedCalDay(new Date(TODAY)); }} className="rounded-lg px-3 py-1.5 text-[11px] font-bold text-white/55 transition-colors hover:bg-white/[0.06] hover:text-white">
                    Today
                  </button>
                  <button type="button" onClick={() => setCalCursor((c) => shiftCursor(c, calRange, 1))} className="flex h-8 w-8 items-center justify-center rounded-lg text-white/50 transition-colors hover:bg-white/[0.06] hover:text-white" aria-label="Next">
                    <Icon icon="bx:chevron-right" className="h-4 w-4" />
                  </button>
                  <span className="px-2 text-sm font-semibold text-white">{calRange === 'month' ? formatMonthLabel(calCursor) : weekLabel(calCursor)}</span>
                </div>
                <LoadChip label="Today" value={queueLoad.today} tone={queueLoad.today > 0 ? 'accent' : 'muted'} icon="bx:calendar-event" />
                <LoadChip label="Week" value={queueLoad.week} tone="muted" icon="bx:calendar-week" />
                <SegmentedControl
                  ariaLabel="Calendar range"
                  size="md"
                  tone="neutral"
                  value={calRange}
                  onChange={(value) => setRange(value as 'month' | 'week')}
                  options={[
                    { value: 'month', icon: 'bx:calendar', label: 'Month' },
                    { value: 'week', icon: 'bx:calendar-week', label: 'Week' },
                  ]}
                />
              </div>
            </div>
          )}

          {/* The reveal stage — remounts on viewStageKey, GSAP blurs in its blocks. */}
          <div key={viewStageKey} ref={stageRef} className={`flex-1 p-3 ${useDocumentScroll ? 'overflow-visible' : 'min-h-0 overflow-hidden'}`}>
          {effectiveView === 'inbox' && (
            <div className="flex h-full min-h-0 flex-col gap-3 lg:flex-row">
              {/* Sidebar — hidden in Outreach (table + preview are the focus, and
                  campaigns are grouped inside the table instead). */}
              {lens !== 'outreach' && (
                <aside className="unibox-sidebar w-full shrink-0 overflow-y-auto rounded-[18px] bg-white/[0.022] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.025)] lg:w-[248px]" data-view-block>
                  <InboxSidebar
                    config={config}
                    groups={groups}
                    effectiveEntities={effectiveEntities}
                    channelCounts={channelCounts}
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    selectedGroupIds={selectedGroupIds}
                    onSelectedGroupIdsChange={setSelectedGroupIds}
                    selectedStatuses={selectedStatuses}
                    onSelectedStatusesChange={setSelectedStatuses}
                    selectedChannels={selectedChannels}
                    onSelectedChannelsChange={setSelectedChannels}
                    onOpenCampaignDetails={onOpenCampaignDetails}
                  />
                </aside>
              )}

              <section
                className="unibox-list flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[18px] bg-[linear-gradient(180deg,rgba(17,18,23,0.74),rgba(8,9,12,0.92))] shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]"
                data-view-block
              >
                {/* Status pill bar — hidden in Outreach queue (the substatus
                    fast-filter on the table replaces it; campaigns are grouped). */}
                {!(lens === 'outreach' && showQueue) && (
                  <StatusPillBar
                    config={config}
                    entities={showQueue ? queuedEntities : inboxEntities}
                    selectedStatuses={selectedStatuses}
                    onToggle={(key) => {
                      const next = new Set(selectedStatuses);
                      if (next.has(key)) next.delete(key);
                      else next.add(key);
                      setSelectedStatuses(next);
                    }}
                  />
                )}
                <div className={`unibox-list-rows min-h-0 flex-1 overflow-y-auto ${showQueue && lens === 'outreach' ? 'p-0' : 'p-2'}`}>
                  {!showQueue && (
                    <>
                      {inboxEntities.map((entity) => (
                        <EntityInboxRow key={entity.lvl1_id} config={config} entity={entity} groups={groups} selected={selectedEntity?.lvl1_id === entity.lvl1_id} onSelect={() => handleSelectEntity(entity, false)} />
                      ))}
                      {inboxEntities.length === 0 && <EmptyState label={actionOnly ? 'All clear - nothing needs you' : 'No inbox matches'} icon={actionOnly ? 'bx:check-circle' : 'bx:search'} />}
                    </>
                  )}
                  {showQueue && (
                    lens === 'outreach' ? (
                      <QueueTable
                        config={config}
                        entities={queuedEntities.filter((e) => selectedSubstatus.size === 0 || selectedSubstatus.has(e.substatus))}
                        countsFrom={queuedEntities}
                        groups={groups}
                        selectedEntityId={selectedEntity?.lvl1_id || null}
                        onSelect={(entity) => handleSelectEntity(entity, true)}
                        onOpenLead={onOpenLead}
                        selectedSubstatus={selectedSubstatus}
                        onSubstatusToggle={toggleSubstatus}
                      />
                    ) : (
                      <>
                        {queuedEntities.map((entity) => (
                          <QueueMessageRow key={`${entity.lvl1_id}-${entity.next_send?.scheduled_at}`} config={config} entity={entity} groups={groups} selected={selectedEntity?.lvl1_id === entity.lvl1_id} onSelect={() => handleSelectEntity(entity, true)} />
                        ))}
                        {queuedEntities.length === 0 && <EmptyState label="No queued messages" icon="bx:time-five" />}
                      </>
                    )
                  )}
                </div>
              </section>

              {/* Inline preview rail for Outreach (SequenceBoard) and Inbox/All
                  (conversation thread). Pipeline keeps the ISR Workspace instead —
                  it's the lead-context detail surface, rendered in the main return.
                  The rail is inline (no scrim) so the list/queue stays visible. */}
              {inlinePreviewRail}
            </div>
          )}

          {effectiveView === 'calendar' && (
            <div className="flex h-full min-h-0 flex-col">
              <FilterBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                groupOptions={groupFilterOptions}
                groupIds={selectedGroupIds}
                onGroupIdsChange={setSelectedGroupIds}
                groupLabel={`${groupLabelLower}s`}
                groupIcon="bx:briefcase-alt-2"
                statusOptions={statusFilterOptions}
                statuses={selectedStatuses}
                onStatusesChange={setSelectedStatuses}
                channelOptions={channelFilterOptions}
                channels={selectedChannels}
                onChannelsChange={setSelectedChannels}
                activeCount={activeFilterCount}
                onReset={resetFilters}
                groupBy={groupBy}
                onGroupByChange={setGroupBy}
              />
              <div className="flex min-h-0 flex-1 gap-2">
                <div className="unibox-calendar min-h-0 min-w-0 flex-1 overflow-y-auto rounded-[18px] bg-black/[0.18] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.022)]" data-view-block>
                  {calRange === 'month' ? (
                    <CalendarMonthGrid
                      groups={groups}
                      entities={queuedEntities}
                      groupKey={config.group_by.key}
                      cursor={calCursor}
                      selectedDay={selectedCalDay}
                      onSelectDay={(d) => setSelectedCalDay(d)}
                      onSelectEntity={(entity) => handleSelectEntity(entity, true)}
                    />
                  ) : (
                    <CalendarWeekStrip
                      groups={groups}
                      entities={queuedEntities}
                      groupKey={config.group_by.key}
                      cursor={calCursor}
                      selectedDay={selectedCalDay}
                      onSelectDay={(d) => setSelectedCalDay(d)}
                      onSelectEntity={(entity) => handleSelectEntity(entity, true)}
                    />
                  )}
                </div>
                {inlinePreviewRail}
              </div>
            </div>
          )}

          {effectiveView === 'kanban' && (
            <div className={`flex flex-col ${useDocumentScroll ? '' : 'h-full min-h-0'}`}>
              <FilterBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                groupOptions={groupFilterOptions}
                groupIds={selectedGroupIds}
                onGroupIdsChange={setSelectedGroupIds}
                groupLabel={`${groupLabelLower}s`}
                groupIcon="bx:briefcase-alt-2"
                statusOptions={statusFilterOptions}
                statuses={selectedStatuses}
                onStatusesChange={setSelectedStatuses}
                channelOptions={channelFilterOptions}
                channels={selectedChannels}
                onChannelsChange={setSelectedChannels}
                activeCount={config.product === 'sales_intelligence' ? activeFilterCount - selectedStatuses.size : activeFilterCount}
                onReset={resetFilters}
                groupBy={groupBy}
                onGroupByChange={setGroupBy}
                groupByOptions={['campaign', 'week', 'day', 'channel', 'substatus']}
                groupByControlLabel="Breakdown"
                groupByCampaignLabel="Overview"
                groupByFirst={config.product === 'sales_intelligence'}
                trailingControls={(
                  <div className="inline-flex min-h-9 items-center gap-1 rounded-xl bg-white/[0.018] p-0.5 pl-2">
                    <span className="inline-flex items-center gap-1.5 pr-1 text-[9px] font-black uppercase tracking-[0.12em] text-white/35">
                      <Icon icon="bx:card" className="h-3.5 w-3.5" />
                      Cards
                    </span>
                    <SegmentedControl
                      ariaLabel="Lead card density"
                      size="sm"
                      tone="neutral"
                      value={leadCardDensity}
                      onChange={(value) => setLeadCardDensity(value as KanbanCardDensity)}
                      className="border-transparent bg-transparent shadow-none"
                      options={[
                        { value: 'detail', icon: 'bx:detail', label: 'Detail' },
                        { value: 'compact', icon: 'bx:list-ul', label: 'Compact' },
                      ]}
                    />
                  </div>
                )}
                hideGroupFilter
                hideStatusFilter={config.product === 'sales_intelligence'}
              />
              <div className={`flex flex-1 gap-2 ${useDocumentScroll ? '' : 'min-h-0'}`}>
                <div className={`unibox-kanban min-w-0 flex-1 rounded-[18px] border border-white/[0.05] bg-black/[0.45] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.025),0_18px_45px_-34px_rgba(0,0,0,0.9)] ${useDocumentScroll ? 'overflow-visible' : 'min-h-0 overflow-hidden'}`} data-view-block>
                  <KanbanStatusView
                    config={config}
                    entities={filteredEntities}
                    groups={groups}
                    visibleStatuses={config.product === 'sales_intelligence' ? NO_KANBAN_STATUS_FILTER : selectedStatuses}
                    selectedEntityId={selectedEntity?.lvl1_id || null}
                    onSelect={(entity) => handleSelectEntity(entity, false)}
                    groupBy={groupBy}
                    cardDensity={leadCardDensity}
                    focusedGroupId={focusedGroupId}
                    onFocusedGroupChange={setFocusedGroupId}
                    onOpenCampaignDetails={onOpenCampaignDetails}
                    currency={currency}
                  />
                </div>
                {inlinePreviewRail}
              </div>
            </div>
          )}
        </div>
      </section>

      {selectedMappedLead && selectedEntity && (
        <ISRWorkspaceDrawer
          open={leadDrawerOpen && !leadModalEntity}
          onClose={closeLeadDrawer}
          lead={selectedMappedLead}
          campaign={selectedCampaign}
          // Contact fields & notes stay read-only on this surface (its feed can
          // be dummy/mock) — but the sales stepper is LIVE when the host wires
          // onSaveSalesState: status/substatus persist immediately through the
          // same update_campaign action Sales Intelligence always used.
          editable={false}
          salesEditable={Boolean(onSaveSalesState)}
          currentUser={READ_ONLY_ISR_ACTOR}
          onSaveSnapshot={ignoreReadOnlyISRSave}
          onSaveSalesState={onSaveSalesState
            ? (sales, notes, updated) => onSaveSalesState(selectedEntity, {
                lead_status: sales.lead_status ?? selectedEntity.status,
                lead_substatus: sales.lead_substatus ?? null,
              }, notes, updated)
            : undefined}
          initialTab={drawerDefaultTab}
          onOpenFullLead={openSelectedLeadDetails}
        />
      )}
      {effectiveView === 'inbox' && previewClosed && !!selectedEntity && (
        <button
          type="button"
          onClick={() => setPreviewClosed(false)}
          className="hidden h-full w-10 shrink-0 flex-col items-center justify-center gap-2 rounded-[20px] bg-[linear-gradient(180deg,rgba(18,19,24,0.76),rgba(9,10,13,0.9))] text-white/45 shadow-[inset_0_1px_0_rgba(255,255,255,0.025)] transition-all hover:bg-white/[0.045] hover:text-white lg:flex"
          title="Show lead info"
        >
          <Icon icon="bx:chevron-left" className="h-5 w-5" />
          <span className="rotate-180 text-[10px] font-black uppercase tracking-[0.22em] [writing-mode:vertical-rl]">Lead info</span>
        </button>
      )}
      </div>

      {/* Lead-details modal (Leads Intelligence full profile). Built from the
          dummy entity via uniBoxEntityToMappedLead; previewMode disables the
          live refetch so the seed object is what renders. */}
      {leadModalEntity && (
        <LeadReportModal
          lead={leadModalData || uniBoxEntityToMappedLead(leadModalEntity)}
          onClose={() => setLeadModalEntity(null)}
          previewMode
        />
      )}
    </div>
  );
};

// --- Cursor helpers for calendar nav ---
const shiftCursor = (cursor: Date, range: 'month' | 'week', dir: number) => {
  const next = new Date(cursor);
  if (range === 'month') next.setMonth(next.getMonth() + dir);
  else next.setDate(next.getDate() + dir * 7);
  return next;
};
const weekLabel = (cursor: Date) => {
  const start = startOfWeek(cursor);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const fmt = (d: Date) => new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(d);
  return `${fmt(start)} – ${fmt(end)}`;
};
const startOfWeek = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay(); // 0 Sun … 6 Sat → make Monday the start
  const diff = (day + 6) % 7;
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const EmptyState: React.FC<{ label: string; icon: string }> = ({ label, icon }) => (
  <div className="flex h-56 flex-col items-center justify-center text-center">
    <Icon icon={icon} className="h-8 w-8 text-white/20" />
    <p className="mt-3 text-sm font-semibold text-white/60">{label}</p>
  </div>
);

// Status filter pills sitting directly above the message list. Active = colored
// (status meaning) with the live count for the current view; inactive = quiet.
// Tapping toggles membership in the shared multi-select, so it stays in sync
// with the sidebar filters.
const StatusPillBar: React.FC<{
  config: UniBoxConfig;
  entities: UniBoxEntity[];
  selectedStatuses: Set<string>;
  onToggle: (key: string) => void;
}> = ({ config, entities, selectedStatuses, onToggle }) => {
  // Count per status within the currently displayed set.
  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    entities.forEach((e) => { map[e.status] = (map[e.status] || 0) + 1; });
    return map;
  }, [entities]);

  // When filtering, collapse to a focus state: show only the active status(es)
  // as removable chips + a "show all" affordance. Hides the rest so the bar
  // becomes a clear "you are filtering by X" signal, not redundant noise.
  const filtering = selectedStatuses.size > 0;

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-1.5 bg-white/[0.016] px-3 py-2.5 shadow-[inset_0_-1px_0_rgba(255,255,255,0.025)]">
      {filtering ? (
        <>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white/35">
            <Icon icon="bx:filter-alt" className="h-3 w-3" />
            Filtered
          </span>
          {/* Lead Pool is a fused option (leads + enriched) — render it as a
              single chip when selected, since it isn't in config.statuses. */}
          {selectedStatuses.has('lead_pool') && (
            <button
              key="lead_pool"
              type="button"
              onClick={() => onToggle('lead_pool')}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl bg-white/[0.06] px-2.5 py-1.5 text-[11px] font-black text-white/86 transition-all"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-white/55" />
              Lead Pool
              <span className="opacity-70">{(counts['leads'] || 0) + (counts['enriched'] || 0)}</span>
              <Icon icon="bx:x" className="h-3 w-3 opacity-70" />
            </button>
          )}
          {config.statuses
            .filter((key) => selectedStatuses.has(key))
            .map((key) => {
              const def = getStatusDefSafe(key);
              const count = counts[key] || 0;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onToggle(key)}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-white/[0.06] px-2.5 py-1.5 text-[11px] font-black text-white/86 transition-all hover:bg-white/[0.08]"
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${def?.dot || 'bg-white/40'}`} />
                  {def?.label || formatLabel(key)}
                  <span className="opacity-70">{count}</span>
                  <Icon icon="bx:x" className="h-3 w-3 opacity-70" />
                </button>
              );
            })}
          <button
            type="button"
            onClick={() => selectedStatuses.forEach((k) => onToggle(k))}
            className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-[11px] font-semibold text-white/45 transition-colors hover:bg-white/[0.05] hover:text-white/75"
          >
            Show all
          </button>
        </>
      ) : (
        <>
          <span className="rounded-xl bg-white/[0.07] px-2.5 py-1.5 text-[11px] font-black text-white">
            All <span className="text-white/50">{entities.length}</span>
          </span>
          {config.statuses.map((key) => {
            const def = getStatusDefSafe(key);
            const count = counts[key] || 0;
            if (count === 0) return null;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onToggle(key)}
                className="inline-flex cursor-pointer items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[11px] font-bold text-white/58 transition-all hover:bg-white/[0.05] hover:text-white/80"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${def?.dot || 'bg-white/40'}`} />
                {def?.label || formatLabel(key)}
                <span className="text-white/30">{count}</span>
              </button>
            );
          })}
        </>
      )}
    </div>
  );
};

// Compact load-summary chip for the calendar toolbar ("Today · 12").
const LoadChip: React.FC<{ label: string; value: number; tone: 'accent' | 'muted'; icon: string }> = ({ label, value, tone, icon }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-bold ${tone === 'accent' && value > 0 ? 'bg-white/[0.065] text-white/82' : 'bg-white/[0.025] text-white/50'}`}>
    <Icon icon={icon} className={`h-3.5 w-3.5 ${tone === 'accent' && value > 0 ? 'text-white/70' : 'text-white/38'}`} />
    {label}
    <span className="text-white/35">{value}</span>
  </span>
);

// --- Campaign status theme (mirrors CampaignIndex.getCampaignStatusTheme) ---
const CAMPAIGN_STATUS_THEME: Record<string, { dot: string; label: string; text: string }> = {
  active: { dot: 'bg-cyan-300', label: 'Active', text: 'text-cyan-200' },
  paused: { dot: 'bg-amber-400', label: 'Paused', text: 'text-amber-300' },
  archived: { dot: 'bg-white/40', label: 'Archived', text: 'text-white/50' },
};

// Human-readable campaign duration, e.g. "Jun 15 → Sep 15" or "Started Jun 20".
const campaignDuration = (group: UniBoxGroup): string => {
  const fmt = (iso: string) => new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(new Date(iso));
  if (group.start && group.done) return `${fmt(group.start)} → ${fmt(group.done)}`;
  if (group.start) return `Started ${fmt(group.start)}`;
  if (group.created) return `Created ${fmt(group.created)}`;
  return '';
};

// --- Inbox sidebar (multi-select filters) ---
const InboxSidebar: React.FC<{
  config: UniBoxConfig;
  groups: UniBoxGroup[];
  effectiveEntities: UniBoxEntity[];
  channelCounts: Record<string, number>;
  searchTerm: string;
  onSearchChange: (v: string) => void;
  selectedGroupIds: Set<string>;
  onSelectedGroupIdsChange: (s: Set<string>) => void;
  selectedStatuses: Set<string>;
  onSelectedStatusesChange: (s: Set<string>) => void;
  selectedChannels: Set<string>;
  onSelectedChannelsChange: (s: Set<string>) => void;
  /** Open a campaign-details surface (e.g. a modal). Outreach shows the affordance. */
  onOpenCampaignDetails?: (groupId: string) => void;
}> = ({ config, groups, effectiveEntities, channelCounts, searchTerm, onSearchChange, selectedGroupIds, onSelectedGroupIdsChange, selectedStatuses, onSelectedStatusesChange, selectedChannels, onSelectedChannelsChange, onOpenCampaignDetails }) => {
  const toggle = (setter: (s: Set<string>) => void, set: Set<string>, value: string) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    setter(next);
  };
  return (
    <>
      <label className="relative block">
        <Icon icon="bx:search" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
        <input value={searchTerm} onChange={(e) => onSearchChange(e.target.value)} placeholder="Search..." className="h-10 w-full rounded-xl bg-white/[0.035] pl-9 pr-3 text-sm text-white outline-none transition-colors placeholder:text-white/32 focus:bg-white/[0.055]" />
      </label>

      <div className="mt-5 space-y-1">
        <div className="flex items-center justify-between px-3">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/30">All</span>
          <span className="text-[11px] text-white/40">{effectiveEntities.length}</span>
        </div>
        <div className="pt-2">
          <p className="px-3 text-[10px] font-black uppercase tracking-[0.2em] text-white/30">{config.group_by.label}s</p>
          <div className="mt-1.5 space-y-0.5">
            {groups.map((group) => {
              const active = selectedGroupIds.has(group.id);
              const colorIndex = getGroupColorIndex(groups, group.id);
              const statusTheme = CAMPAIGN_STATUS_THEME[group.status || 'active'] || CAMPAIGN_STATUS_THEME.active;
              const duration = campaignDuration(group);
              return (
                <div
                  key={group.id}
                  className={`group/cmp rounded-lg px-2.5 py-2 transition-all ${active ? 'bg-white/[0.07] shadow-[inset_0_1px_0_rgba(255,255,255,0.035)]' : 'hover:bg-white/[0.035]'}`}
                >
                  <button
                    onClick={() => toggle(onSelectedGroupIdsChange, selectedGroupIds, group.id)}
                    className="flex w-full items-center justify-between gap-2 text-left"
                  >
                    <span className="inline-flex min-w-0 items-center gap-2">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${GROUP_DOT[colorIndex % GROUP_DOT.length]}`} />
                      <span className={`truncate text-sm font-semibold ${active ? 'text-white' : 'text-gray-200'}`}>{group.name}</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      {group.needs_human > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/12 px-1.5 py-0.5 text-[10px] font-bold text-amber-100">{group.needs_human}</span>}
                      <span className="text-[11px] font-bold text-white/45">{group.total}</span>
                    </span>
                  </button>
                  {/* Status + duration row — Outreach context only (when the
                      details callback exists). Identifies the campaign at a glance. */}
                  {onOpenCampaignDetails && (
                    <div className="mt-1.5 flex items-center justify-between gap-2 pl-4">
                      <span className="inline-flex min-w-0 items-center gap-1.5">
                        <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.1em] ${statusTheme.text}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${statusTheme.dot}`} />
                          {statusTheme.label}
                        </span>
                        {duration && <span className="truncate text-[10px] text-white/35">{duration}</span>}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onOpenCampaignDetails(group.id); }}
                        className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-bold text-white/35 opacity-0 transition-opacity hover:text-white/75 group-hover/cmp:opacity-100"
                        title="Campaign details"
                      >
                        <Icon icon="bx:info-circle" className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {/* Keep automation meta referenced so TS doesn't flag it unused */}
            <span className="hidden">{AUTOMATION_META[config.default_automation].label}</span>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <p className="px-3 text-[10px] font-black uppercase tracking-[0.2em] text-white/30">Channels</p>
        <div className="mt-2 space-y-0.5">
          {config.channels.map((channel) => {
            const meta = getChannelMeta(channel);
            const active = selectedChannels.has(channel);
            return (
              <button key={channel} onClick={() => toggle(onSelectedChannelsChange, selectedChannels, channel)} className={`flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-left text-sm transition-all ${active ? 'bg-white/[0.065] text-white' : 'text-gray-400 hover:bg-white/[0.03] hover:text-gray-200'}`}>
                <span className="inline-flex min-w-0 items-center gap-2"><Icon icon={meta.icon} className={`h-4 w-4 ${meta.accent}`} /><span className="truncate">{meta.label}</span></span>
                <span className="text-[10px] text-white/35">{channelCounts[channel] || 0}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

const EntityInboxRow: React.FC<{ config: UniBoxConfig; entity: UniBoxEntity; groups: UniBoxGroup[]; selected: boolean; onSelect: () => void }> = ({ config, entity, groups, selected, onSelect }) => {
  const st = statusStyles(entity.status);
  const escalated = entity.control_state === 'escalated';
  return (
    <button onClick={onSelect} className={`unibox-list-row group/list-row grid w-full grid-cols-[34px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-left transition-all ${selected ? 'bg-white/[0.07] shadow-[inset_3px_0_0_rgba(255,255,255,0.28)]' : escalated ? 'bg-amber-400/[0.045] hover:bg-amber-400/[0.075]' : 'bg-transparent hover:bg-white/[0.035]'}`}>
      <div className={`flex h-8 w-8 items-center justify-center rounded-lg text-[11px] font-black ${escalated ? 'bg-amber-400/10 text-amber-100/85' : ''}`} style={escalated ? undefined : { backgroundColor: 'rgba(255,255,255,0.04)' }}>
        <span className={st.text}>{getInitials(entity.client_name)}</span>
      </div>
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`truncate text-sm font-bold ${selected ? 'text-white' : 'text-gray-100'}`}>{entity.client_name}</span>
          {entity.company && <span className="truncate text-[11px] text-white/32">{entity.company}</span>}
        </div>
        <p className="mt-0.5 truncate text-xs text-gray-500">{entity.last_message_snippet}</p>
        <div className="mt-1 flex min-w-0 items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-md bg-white/[0.045] px-1.5 py-0.5 text-[10px] font-bold text-white/58">
            <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
            {getStatusLabel(config, entity.status)}
          </span>
          {entity.needs_reply && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-white/50"><Icon icon="bx:mail-send" className="h-3 w-3" />Replied</span>}
          {entity.next_send && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-white/35"><Icon icon="bx:time-five" className="h-3 w-3" />Queued</span>}
        </div>
      </div>
      {/* Right edge: the action that belongs to this item. Escalated → a clear
          "Take over" pill; otherwise just the time. Keeps the "needs you"
          signal connected to its action instead of a floating badge. */}
      <div className="flex flex-col items-end gap-1">
        {escalated ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.075] px-2 py-1 text-[10px] font-bold text-white/86 transition-colors group-hover/list-row:bg-white/[0.1]">
            <Icon icon="bx:error-circle" className="h-3 w-3" />
            Take over
          </span>
        ) : (
          <span className="text-[10px] text-white/35">{formatRelativeDay(entity.last_activity_at)}</span>
        )}
        {entity.control_state === 'human_takeover' && (
          <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.055] px-1.5 py-0.5 text-[9px] font-bold text-white/70">
            <Icon icon="bx:user" className="h-2.5 w-2.5 text-white/45" />You
          </span>
        )}
      </div>
    </button>
  );
};

const QueueMessageRow: React.FC<{ config: UniBoxConfig; entity: UniBoxEntity; groups: UniBoxGroup[]; selected: boolean; onSelect: () => void }> = ({ config, entity, groups, selected, onSelect }) => {
  if (!entity.next_send) return null;
  const st = statusStyles(entity.status);
  const channelMeta = getChannelMeta(entity.next_send.channel);
  const groupId = String(entity[config.group_by.key] || '');
  const groupName = groups.find((group) => group.id === groupId)?.name || groupId || config.group_by.label;
  const colorIndex = getGroupColorIndex(groups, groupId);
  const sequenceLabel = config.sequence_labels?.[entity.next_send.sequence_step] || `Step ${entity.next_send.sequence_step}`;
  // Send timing shown in the LEAD's local timezone — outreach is global, so
  // this is the wall-clock the lead actually experiences. Weekday + day on top,
  // local time below (sender time was misleading for cross-zone sends).
  const timezone = resolveTimezone(entity);
  const hasLocalTz = timezone !== 'UTC' || typeof entity.timezone === 'string';
  return (
    <button onClick={onSelect} className={`unibox-list-row group/list-row mb-1 grid w-full grid-cols-[84px_minmax(0,1fr)_auto] gap-2.5 rounded-xl px-2.5 py-2.5 text-left transition-all ${selected ? 'bg-white/[0.07] shadow-[inset_3px_0_0_rgba(255,255,255,0.28)]' : 'bg-transparent hover:bg-white/[0.035]'}`}>
      <div className="text-left">
        <div className="text-[10px] font-black uppercase tracking-[0.14em] text-white/70">{formatWeekday(entity.next_send.scheduled_at)}</div>
        <div className="mt-0.5 text-[11px] font-bold text-white/55">{formatDayMonth(entity.next_send.scheduled_at)}</div>
        <div className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-white/40">
          <Icon icon="bx:time-five" className="h-3 w-3" />
          {hasLocalTz ? formatLocalSendTime(entity.next_send.scheduled_at, timezone) : formatShortTime(entity.next_send.scheduled_at)}
        </div>
      </div>
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`h-2 w-2 shrink-0 rounded-full ${GROUP_DOT[colorIndex % GROUP_DOT.length]}`} />
          <Icon icon={channelMeta.icon} className={`h-3.5 w-3.5 shrink-0 ${channelMeta.accent}`} />
          <span className="truncate text-sm font-bold text-white">{entity.client_name}</span>
          <span className="truncate text-[11px] text-white/32">{groupName}</span>
        </div>
        <p className="mt-0.5 truncate text-xs text-gray-500">{entity.next_send.preview}</p>
        <div className="mt-1 flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-md bg-white/[0.045] px-1.5 py-0.5 text-[10px] font-bold text-white/58">
            <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
            {getStatusLabel(config, entity.status)}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/35">{sequenceLabel}</span>
        </div>
      </div>
      <div className="flex items-center"><Icon icon="bx:chevron-right" className="h-4 w-4 text-white/20" /></div>
    </button>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// QueueTable — the dense Outreach queue. One row per lead/company: Lead (id) ·
// Campaign · Status · Substatus ("Message 2/3") · Outcome · Next send (local
// time + countdown). No "last message" line — this is the send plan, not a
// conversation. Sorted by next-send; clicking opens the SequenceBoard preview.
// ──────────────────────────────────────────────────────────────────────────
const countdown = (scheduledAt: string): string => {
  const ms = new Date(scheduledAt).getTime() - TODAY.getTime();
  if (ms <= 0) return 'now';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `in ${hours}h`;
  const days = Math.round(hours / 24);
  return `in ${days}d`;
};

const substatusProgress = (config: UniBoxConfig, entity: UniBoxEntity): string => {
  // Prefer real per-channel sequence progress ("Message 2/3"); fall back to the
  // substatus label, then the sequence step from next_send.
  const prog = entity.sequenceProgress?.[0];
  if (prog) return `Message ${prog.sent}/${prog.total}`;
  const subDef = config.substatusByStage[entity.status]?.find((s) => s.key === entity.substatus);
  if (subDef) return subDef.label;
  if (entity.next_send) return `Step ${entity.next_send.sequence_step}`;
  return '—';
};

// Shared column grid for the queue table — keeps the header and rows aligned.
const QUEUE_GRID = 'grid-cols-[minmax(220px,1.5fr)_112px_108px_minmax(170px,1.2fr)_minmax(110px,0.8fr)_132px]';

const QueueTable: React.FC<{
  config: UniBoxConfig;
  entities: UniBoxEntity[];
  /** Unfiltered entity set for stable filter counts (so chips never disappear). */
  countsFrom?: UniBoxEntity[];
  groups: UniBoxGroup[];
  selectedEntityId: number | null;
  onSelect: (e: UniBoxEntity) => void;
  onOpenLead?: (e: UniBoxEntity) => void;
  selectedSubstatus?: Set<string>;
  onSubstatusToggle?: (key: string) => void;
}> = ({ config, entities, countsFrom, groups, selectedEntityId, onSelect, onOpenLead, selectedSubstatus, onSubstatusToggle }) => {
  // Collapsed campaign groups (persisted within the table's lifetime).
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  if ((countsFrom || entities).length === 0) return <EmptyState label="Nothing scheduled" icon="bx:time-five" />;

  // Substatus counts are computed from ALL outreaching entities (before the
  // substatus filter), so chips keep stable counts and never disappear when
  // you pick one — it's multi-select, you can pick several at once.
  const subCounts: Record<string, number> = {};
  (countsFrom || entities).forEach((e) => { if (e.status === 'outreaching' && e.substatus) subCounts[e.substatus] = (subCounts[e.substatus] || 0) + 1; });
  const subDefs = config.substatusByStage.outreaching || [];
  const filtering = (selectedSubstatus?.size || 0) > 0;
  const clearAll = () => selectedSubstatus?.forEach((k) => onSubstatusToggle?.(k));

  return (
    <div className="flex h-full flex-col">
      {/* Substatus multi-select fast-filter — chips stay visible (dimmed at 0)
          so picking one never hides the others. "All" clears the selection. */}
      {subDefs.length > 0 && onSubstatusToggle && (
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 bg-white/[0.016] px-3 py-2.5 shadow-[inset_0_-1px_0_rgba(255,255,255,0.025)]">
          <button
            type="button"
            onClick={clearAll}
            disabled={!filtering}
            className={`cursor-pointer rounded-xl px-2.5 py-1.5 text-[11px] font-black transition-all ${!filtering ? 'bg-white/[0.075] text-white' : 'text-white/40 hover:bg-white/[0.05] hover:text-white/75'}`}
          >
            All
          </button>
          {subDefs.map((s) => {
            const count = subCounts[s.key] || 0;
            const active = selectedSubstatus?.has(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => onSubstatusToggle(s.key)}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-[11px] font-black transition-all ${
                  active
                    ? 'bg-white/[0.075] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
                    : count === 0
                      ? 'text-white/25'
                      : 'text-white/55 hover:bg-white/[0.05] hover:text-white/80'
                }`}
              >
                {active && <Icon icon="bx:check" className="h-3 w-3" />}
                {s.label}
                <span className={active ? 'text-white/65' : 'text-white/35'}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Rows — grouped by campaign. The column header repeats under each
          expanded campaign so the columns read in context. */}
      {/* Rows — grouped by campaign. */}
      <div className="min-h-0 flex-1 overflow-auto">
        {(() => {
          // Preserve campaign order from `groups`; bucket entities by campaign.
          const byGroup = new Map<string, UniBoxEntity[]>();
          entities.forEach((e) => {
            const gk = String(e[config.group_by.key] || '');
            if (!byGroup.has(gk)) byGroup.set(gk, []);
            byGroup.get(gk)!.push(e);
          });
          const ordered = groups.map((g) => g.id).filter((id) => byGroup.has(id));
          const orphanKeys = [...byGroup.keys()].filter((k) => !groups.some((g) => g.id === k));
          orphanKeys.forEach((k) => ordered.push(k));
          if (ordered.length === 0) return <EmptyState label="No scheduled leads match" icon="bx:filter-alt" />;
          const row = (entity: UniBoxEntity) => {
            if (!entity.next_send) return null;
            const st = statusStyles(entity.status);
            const def = getStatusDefSafe(entity.status);
            const timezone = resolveTimezone(entity);
            const hasLocalTz = timezone !== 'UTC' || typeof entity.timezone === 'string';
            const outcomeDef = config.outcomes.find((o) => o.key === entity.outcome);
            const selected = selectedEntityId === entity.lvl1_id;
            const prog = entity.sequenceProgress?.slice().sort((a, b) => b.sent - a.sent)[0];
            const sent = prog?.sent ?? 0;
            const total = prog?.total ?? 3;
            const pct = Math.min(100, (sent / total) * 100);
            return (
              <div
                key={`${entity.lvl1_id}-${entity.next_send.scheduled_at}`}
                onClick={() => onSelect(entity)}
                className={`unibox-list-row grid min-w-[900px] cursor-pointer ${QUEUE_GRID} items-center gap-3 px-4 py-3 text-left shadow-[inset_0_-1px_0_rgba(255,255,255,0.024)] transition-all ${
                  selected
                    ? 'bg-white/[0.07] shadow-[inset_3px_0_0_rgba(255,255,255,0.28),inset_0_-1px_0_rgba(255,255,255,0.028)]'
                    : 'hover:bg-white/[0.035]'
                }`}
              >
                {/* Lead — name + #id inline (opens lead details), company below. */}
                <span className="flex min-w-0 items-center gap-2">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-white/28" />
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onOpenLead?.(entity); }}
                        className="max-w-full truncate text-sm font-black text-white hover:text-white/82 hover:underline"
                        title="Open lead details"
                      >
                        {entity.client_name}
                      </button>
                      <span className="shrink-0 text-[10px] font-bold text-white/30">#{entity.lvl1_id}</span>
                    </span>
                    {entity.company && <span className="mt-0.5 block truncate text-[11px] font-semibold text-[#7D879B]">{entity.company}</span>}
                  </span>
                </span>
                {/* Channels — icons of the channels planned for this lead. */}
                <span className="flex flex-wrap items-center gap-1">
                  {entity.channels.slice(0, 4).map((c) => {
                    const meta = getChannelMeta(c);
                    return <span key={c} title={meta.label} className="flex h-6 w-6 items-center justify-center rounded-md bg-white/[0.045]"><Icon icon={meta.icon} className={`h-3.5 w-3.5 ${meta.accent}`} /></span>;
                  })}
                  {entity.channels.length > 4 && <span className="text-[9px] text-white/35">+{entity.channels.length - 4}</span>}
                </span>
                <span className="inline-flex w-fit cursor-pointer items-center gap-1 rounded-lg bg-white/[0.045] px-2 py-1 text-[10px] font-black uppercase tracking-[0.04em] text-white/58">
                  <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
                  {def?.label || formatLabel(entity.status)}
                </span>
                {/* Sequence — visual progress bar + substatus label. */}
                <span className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-white/[0.08]">
                      <div className="h-full rounded-full bg-gradient-to-r from-white/70 to-white/40" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="shrink-0 text-[10px] font-black text-white/68">{sent}/{total}</span>
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-[0.12em] text-white/35">{substatusProgress(config, entity)}</span>
                </span>
                <span className="truncate text-xs font-semibold text-white/45">{outcomeDef?.label || '-'}</span>
                <span className="flex flex-col items-end">
                  <span className="text-xs font-black text-white/75">{hasLocalTz ? formatLocalSendTime(entity.next_send.scheduled_at, timezone) : formatShortTime(entity.next_send.scheduled_at)}</span>
                  <span className="mt-0.5 text-[10px] font-black text-white/55">{countdown(entity.next_send.scheduled_at)}</span>
                </span>
              </div>
            );
          };
          return ordered.map((gk) => {
            const group = groups.find((g) => g.id === gk);
            const items = byGroup.get(gk) || [];
            const colorIndex = group ? getGroupColorIndex(groups, gk) : 0;
            // Per-campaign performance, derived from the leads themselves.
            const leadsCount = items.length;
            const messagesSent = items.reduce((sum, e) => {
              const p = e.sequenceProgress?.slice().sort((a, b) => b.sent - a.sent)[0];
              return sum + (p?.sent ?? 0);
            }, 0);
            const messagesTotal = items.reduce((sum, e) => {
              const p = e.sequenceProgress?.slice().sort((a, b) => b.sent - a.sent)[0];
              return sum + (p?.total ?? 3);
            }, 0) || 1;
            const upcomingWeek = items.filter((e) => e.next_send && new Date(e.next_send.scheduled_at).getTime() <= TODAY.getTime() + 7 * 86400000).length;
            const engaged = items.filter((e) => e.status === 'engaged').length;
            const isCollapsed = collapsedGroups.has(gk);
            const statusTheme = CAMPAIGN_STATUS_THEME[group?.status || 'active'] || CAMPAIGN_STATUS_THEME.active;
            return (
              <div key={gk || 'ungrouped'}>
                {/* Campaign group header — collapsible, with performance metrics. */}
                <button
                  type="button"
                  onClick={() => setCollapsedGroups((prev) => { const n = new Set(prev); if (n.has(gk)) n.delete(gk); else n.add(gk); return n; })}
                  className="sticky top-0 z-10 flex min-w-[900px] items-center gap-3 bg-[#101116]/92 px-4 py-2.5 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.03),inset_0_-1px_0_rgba(255,255,255,0.026)] backdrop-blur transition-colors hover:bg-[#141720]/92"
                  style={getGroupSummaryHeaderStyle(colorIndex)}
                >
                  <Icon icon={isCollapsed ? 'bx:chevron-right' : 'bx:chevron-down'} className="h-3.5 w-3.5 shrink-0 text-white/45" />
                  <span className="inline-flex min-w-0 items-center gap-2">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${group ? GROUP_DOT[colorIndex % GROUP_DOT.length] : 'bg-white/30'}`} />
                    <span className="truncate text-sm font-black tracking-tight text-white">{group?.name || 'Ungrouped'}</span>
                    {group?.status && (
                      <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.1em] ${statusTheme.text}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${statusTheme.dot}`} />
                        {statusTheme.label}
                      </span>
                    )}
                  </span>
                  {/* Performance metrics. */}
                  <span className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-0.5 text-[10px] font-black">
                    <span className="inline-flex items-center gap-1 text-white/55" title="Leads in outreach">
                      <Icon icon="bx:user" className="h-3 w-3 text-white/35" />{leadsCount}
                    </span>
                    <span className="inline-flex items-center gap-1 text-white/62" title="Sequence messages sent / planned">
                      <Icon icon="bx:send" className="h-3 w-3 text-white/38" />{messagesSent}/{messagesTotal}
                    </span>
                    <span className="inline-flex items-center gap-1 text-white/62" title="Sends in the next 7 days">
                      <Icon icon="bx:calendar-week" className="h-3 w-3 text-white/38" />{upcomingWeek}
                    </span>
                    {engaged > 0 && (
                      <span className="inline-flex items-center gap-1 text-white/62" title="Engaged">
                        <Icon icon="bx:conversation" className="h-3 w-3 text-white/38" />{engaged}
                      </span>
                    )}
                  </span>
                </button>
                {!isCollapsed && (
                  <>
                    {/* Column header — repeats under each expanded campaign. */}
                    <div className={`grid min-w-[900px] ${QUEUE_GRID} items-center gap-3 bg-white/[0.018] px-4 py-2 text-[9px] font-black uppercase tracking-[0.18em] text-[#7D879B] shadow-[inset_0_-1px_0_rgba(255,255,255,0.024)]`}>
                      <span>Lead</span>
                      <span>Channels</span>
                      <span>Status</span>
                      <span>Sequence</span>
                      <span>Outcome</span>
                      <span className="text-right">Next send</span>
                    </div>
                    {items.map(row)}
                  </>
                )}
              </div>
            );
          });
        })()}
      </div>
    </div>
  );
};

export default UniBox;

// ──────────────────────────────────────────────────────────────────────────
// Calendar views — month grid + week strip (nexus-ai-os style)
// ──────────────────────────────────────────────────────────────────────────

// Build the 6×7 month grid (Monday-first) for the given cursor month.
const buildMonthCells = (cursor: Date): Date[] => {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = startOfWeek(first);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  return cells;
};

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface CalBucket {
  key: string;
  name: string;
  colorIndex: number;
  entities: UniBoxEntity[];
}

// Stable string → int hash, used to pick a consistent color per status key.
const hashCode = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return h;
};

// Bucket queued entities by day → group (campaign for Sales, account/inbox
// for AI Workers). Color is always the native group — the sender is the only
// dimension that explains why a day is loaded.
const bucketByDay = (groupKey: string, groups: UniBoxGroup[], entities: UniBoxEntity[]) => {
  const map = new Map<string, Map<string, CalBucket>>();
  entities.forEach((entity) => {
    if (!entity.next_send) return;
    const date = new Date(entity.next_send.scheduled_at);
    const dayKey = date.toDateString();
    const gKey = String(entity[groupKey] || 'none');
    const gName = groups.find((g) => g.id === gKey)?.name || gKey;
    const colorIndex = getGroupColorIndex(groups, gKey);
    if (!map.has(dayKey)) map.set(dayKey, new Map());
    const dayMap = map.get(dayKey)!;
    const bucket = dayMap.get(gKey) || { key: gKey, name: gName, colorIndex, entities: [] };
    bucket.entities.push(entity);
    dayMap.set(gKey, bucket);
  });
  return map;
};

const CalendarMonthGrid: React.FC<{
  groups: UniBoxGroup[];
  entities: UniBoxEntity[];
  groupKey: string;
  cursor: Date;
  selectedDay: Date;
  onSelectDay: (d: Date) => void;
  onSelectEntity: (e: UniBoxEntity) => void;
}> = ({ groups, entities, groupKey, cursor, selectedDay, onSelectDay, onSelectEntity }) => {
  const cells = useMemo(() => buildMonthCells(cursor), [cursor]);
  const byDay = useMemo(() => bucketByDay(groupKey, groups, entities), [entities, groupKey, groups]);

  // Legend = distinct active campaigns across the visible month.
  const legend = useMemo(() => {
    const seen = new Map<string, CalBucket>();
    byDay.forEach((dayMap) => dayMap.forEach((b) => { if (!seen.has(b.key)) seen.set(b.key, b); }));
    return Array.from(seen.values()).sort((a, b) => b.entities.length - a.entities.length);
  }, [byDay]);

  return (
    <div className="flex h-full flex-col">
      <div className="grid grid-cols-7 gap-[3px]">
        {WEEKDAY_LABELS.map((d) => (
          <div key={d} className="px-1 py-2 text-center text-[9px] font-bold uppercase tracking-[0.18em] text-white/30">{d}</div>
        ))}
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-6 gap-[3px]">
        {cells.map((date) => {
          const buckets = Array.from(byDay.get(date.toDateString())?.values() || []);
          const total = buckets.reduce((sum, b) => sum + b.entities.length, 0);
          const isToday = isSameDay(date, TODAY);
          const isSelected = isSameDay(date, selectedDay);
          const outside = date.getMonth() !== cursor.getMonth();
          return (
            <button
              key={date.toISOString()}
              type="button"
              onClick={() => onSelectDay(date)}
              className={`relative flex min-h-[54px] flex-col items-start gap-1.5 rounded-lg p-1.5 text-left transition-all ${
                isSelected ? 'bg-white/[0.065] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]' : isToday ? 'bg-white/[0.04] hover:bg-white/[0.055]' : outside ? 'opacity-30 hover:bg-white/[0.03]' : 'hover:bg-white/[0.035]'
              }`}
            >
              <span className={`text-xs ${isToday ? 'font-bold text-white/88' : isSelected ? 'text-white' : 'text-white/55'}`}>{date.getDate()}</span>
              {total > 0 && (
                <span className="flex flex-wrap gap-[3px]">
                  {buckets.slice(0, 4).map((b) => (
                    <span
                      key={b.key}
                      title={`${b.name}: ${b.entities.length}`}
                      className={`h-1.5 w-1.5 rounded-full ${GROUP_DOT[b.colorIndex % GROUP_DOT.length]} ${GROUP_SHADOW[b.colorIndex % GROUP_SHADOW.length]}`}
                    />
                  ))}
                </span>
              )}
              {total > 4 && <em className="absolute bottom-1 right-1.5 text-[9px] not-italic text-white/40">+{total - 4}</em>}
            </button>
          );
        })}
      </div>

      {/* Selected day agenda */}
      <SelectedDayAgenda selectedDay={selectedDay} byDay={byDay} onSelectEntity={onSelectEntity} />

      {legend.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 px-1">
          {legend.map((b) => (
            <span key={b.key} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white/55">
              <span className={`h-2 w-2 rounded-full ${GROUP_DOT[b.colorIndex % GROUP_DOT.length]}`} />
              {b.name}<span className="text-white/30">{b.entities.length}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const SelectedDayAgenda: React.FC<{
  selectedDay: Date;
  byDay: Map<string, Map<string, CalBucket>>;
  onSelectEntity: (e: UniBoxEntity) => void;
}> = ({ selectedDay, byDay, onSelectEntity }) => {
  const buckets = Array.from(byDay.get(selectedDay.toDateString())?.values() || []).sort((a, b) => {
    const at = a.entities[0]?.next_send?.scheduled_at || '';
    const bt = b.entities[0]?.next_send?.scheduled_at || '';
    return at.localeCompare(bt);
  });
  const items = buckets.flatMap((b) => b.entities.map((e) => ({ e, colorIndex: b.colorIndex }))).sort((a, b) => (a.e.next_send?.scheduled_at || '').localeCompare(b.e.next_send?.scheduled_at || ''));

  return (
    <div className="mt-4 pt-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.024)]">
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/35">{items.length} scheduled</span>
        <b className="text-sm font-semibold text-white">{new Intl.DateTimeFormat('en', { weekday: 'long', month: 'short', day: 'numeric' }).format(selectedDay)}</b>
      </div>
      {items.length === 0 ? (
        <p className="rounded-lg bg-white/[0.02] px-3 py-3 text-[11px] text-white/35">Nothing scheduled this day.</p>
      ) : (
        <div className="grid max-h-[160px] gap-1 overflow-y-auto sm:grid-cols-2">
          {items.slice(0, 12).map(({ e, colorIndex }) => (
            <button key={`${e.lvl1_id}-${e.next_send?.scheduled_at}`} type="button" onClick={() => onSelectEntity(e)} className="flex items-center justify-between gap-2 rounded-lg bg-white/[0.02] px-2 py-1.5 text-left text-[11px] transition-colors hover:bg-white/[0.06]">
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${GROUP_DOT[colorIndex % GROUP_DOT.length]}`} />
                <span className="truncate text-white/75">{e.client_name}</span>
              </span>
              <span className="shrink-0 font-semibold text-white/45">{e.next_send ? formatShortTime(e.next_send.scheduled_at) : ''}</span>
            </button>
          ))}
          {items.length > 12 && <span className="px-2 text-[10px] text-white/30">+{items.length - 12} more</span>}
        </div>
      )}
    </div>
  );
};

const CalendarWeekStrip: React.FC<{
  groups: UniBoxGroup[];
  entities: UniBoxEntity[];
  groupKey: string;
  cursor: Date;
  selectedDay: Date;
  onSelectDay: (d: Date) => void;
  onSelectEntity: (e: UniBoxEntity) => void;
}> = ({ groups, entities, groupKey, cursor, selectedDay, onSelectDay, onSelectEntity }) => {
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => { const d = startOfWeek(cursor); d.setDate(d.getDate() + i); return d; }), [cursor]);
  const byDay = useMemo(() => bucketByDay(groupKey, groups, entities), [entities, groupKey, groups]);

  return (
    <div className="grid h-full grid-cols-7 gap-2">
      {days.map((date) => {
        const buckets = Array.from(byDay.get(date.toDateString())?.values() || []);
        const items = buckets.flatMap((b) => b.entities.map((e) => ({ e, colorIndex: b.colorIndex }))).sort((a, b) => (a.e.next_send?.scheduled_at || '').localeCompare(b.e.next_send?.scheduled_at || ''));
        const isToday = isSameDay(date, TODAY);
        const isSelected = isSameDay(date, selectedDay);
        return (
          <div key={date.toISOString()} className="flex min-h-0 flex-col">
            <button type="button" onClick={() => onSelectDay(date)} className={`mb-2 shrink-0 rounded-lg px-2 py-2 text-left transition-all ${isSelected ? 'bg-white/[0.065] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]' : isToday ? 'bg-white/[0.04]' : 'bg-white/[0.015] hover:bg-white/[0.04]'}`}>
              <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-white/40">{WEEKDAY_LABELS[(date.getDay() + 6) % 7]}</div>
              <div className={`text-base font-semibold ${isToday ? 'text-white/88' : 'text-white/80'}`}>{date.getDate()}</div>
              <div className="text-[10px] text-white/35">{items.length} queued</div>
            </button>
            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
              {items.map(({ e, colorIndex }) => (
                <button key={`${e.lvl1_id}-${e.next_send?.scheduled_at}`} type="button" onClick={() => onSelectEntity(e)} className={`block w-full rounded-md border-l-2 px-2 py-1.5 text-left text-[10px] transition-colors hover:bg-white/[0.06]`} style={{ borderColor: 'transparent' }}>
                  <span className={`block truncate font-semibold text-white/80`}>{e.client_name}</span>
                  <span className={`flex items-center gap-1 text-[9px] text-white/40`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${GROUP_DOT[colorIndex % GROUP_DOT.length]}`} />
                    {e.next_send ? formatShortTime(e.next_send.scheduled_at) : ''}
                  </span>
                </button>
              ))}
              {items.length === 0 && <p className="px-2 py-3 text-[10px] text-white/25">—</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Kanban-as-home — the global funnel board. Columns come from the module's
// config.funnelColumns (Sales = the 6 lead stages; each AI Worker = its own
// status sequence), so the SAME component renders a different board per module.
//
// Sales Pipeline specifics:
//  - Leads column renders a COUNT-ONLY metric tile (the pool can be thousands;
//    listing cards would overwhelm the board).
//  - Engaged column expands into sub-status mini-columns (the funnel-within-
//    the-funnel) so reps can see leads moving across the engaged process.
//  - A campaign focus strip above the board isolates one campaign (or All),
//    and doubles as the color-dot legend on each card.
// ──────────────────────────────────────────────────────────────────────────
const KANBAN_PAGE_SIZE = 12;
const NO_KANBAN_STATUS_FILTER = new Set<string>();

const hexToRgba = (hex: string, alpha: number) => {
  const normalized = hex.replace('#', '');
  if (!/^[\da-f]{6}$/i.test(normalized)) return `rgba(255,255,255,${alpha})`;

  const value = Number.parseInt(normalized, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r},${g},${b},${alpha})`;
};

const getKanbanHeaderStyle = (accentColor?: string): React.CSSProperties | undefined => {
  if (!accentColor) return undefined;

  return {
    background: `linear-gradient(180deg, ${hexToRgba(accentColor, 0.66)}, ${hexToRgba(accentColor, 0.5)})`,
    borderColor: hexToRgba(accentColor, 0.36),
    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.12), 0 10px 24px -20px ${hexToRgba(accentColor, 0.72)}`,
  };
};

const getKanbanSubstatusHeaderStyle = (accentColor?: string): React.CSSProperties | undefined => {
  if (!accentColor) return undefined;

  return {
    background: `linear-gradient(180deg, ${hexToRgba(accentColor, 0.34)}, ${hexToRgba(accentColor, 0.22)})`,
    borderColor: hexToRgba(accentColor, 0.22),
    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.08), 0 8px 18px -17px ${hexToRgba(accentColor, 0.5)}`,
  };
};

const getGroupSummaryHeaderStyle = (colorIndex?: number): React.CSSProperties => {
  const accent = GROUP_HEX[(colorIndex ?? 0) % GROUP_HEX.length];
  return {
    background: `linear-gradient(90deg, ${hexToRgba(accent, 0.18)}, ${hexToRgba(accent, 0.07)} 58%, rgba(0,0,0,0.18))`,
    borderColor: hexToRgba(accent, 0.16),
    boxShadow: `inset 3px 0 0 ${hexToRgba(accent, 0.62)}, inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 18px -18px ${hexToRgba(accent, 0.55)}`,
  };
};

const getStatusGroupHeaderStyle = (accentColor?: string): React.CSSProperties | undefined => {
  if (!accentColor) return undefined;

  return {
    background: `linear-gradient(90deg, ${hexToRgba(accentColor, 0.2)}, ${hexToRgba(accentColor, 0.08)} 58%, rgba(0,0,0,0.18))`,
    borderColor: hexToRgba(accentColor, 0.18),
    boxShadow: `inset 3px 0 0 ${hexToRgba(accentColor, 0.48)}, inset 0 1px 0 rgba(255,255,255,0.04), 0 8px 18px -18px ${hexToRgba(accentColor, 0.5)}`,
  };
};

const getKanbanCardSubstatus = (config: UniBoxConfig, entity: UniBoxEntity): { label: string; icon?: string; transitional?: boolean } | null => {
  const substatuses = config.substatusByStage[entity.status] || [];
  if (substatuses.length === 0) return null;

  const substatus = typeof entity.substatus === 'string' ? entity.substatus.trim() : '';
  const outcome = typeof entity.outcome === 'string' ? entity.outcome.trim() : '';
  const key = substatus || (entity.status === 'discarded' ? outcome : '');
  if (!key) return null;

  const def = substatuses.find((item) => item.key === key);
  return { label: def?.label || formatLabel(key), icon: def?.icon, transitional: def?.transitional };
};

// Substatus pill — a modern glass treatment tinted by the parent status color,
// but deliberately LOWER saturation than the status itself (soft /8 bg, /15
// border, slightly muted text). The visual hierarchy reads: status is the loud
// anchor, substatus is the quieter sub-step inside it. Keyed by status so each
// funnel stage gets its own family; `default` is the neutral fallback.
const SUBSTATUS_GLASS: Record<string, string> = {
  enriched: 'border-blue-400/15 bg-blue-400/[0.08] text-blue-200/80',
  outreaching: 'border-orange-400/15 bg-orange-400/[0.08] text-orange-200/80',
  outreached: 'border-purple-400/15 bg-purple-400/[0.08] text-purple-200/80',
  engaged: 'border-pink-400/15 bg-pink-400/[0.08] text-pink-200/80',
  proposal: 'border-cyan-400/15 bg-cyan-400/[0.08] text-cyan-200/80',
  converted: 'border-emerald-400/15 bg-emerald-400/[0.08] text-emerald-200/80',
  discarded: 'border-gray-400/15 bg-gray-400/[0.08] text-gray-300/80',
  default: 'border-white/10 bg-white/[0.05] text-white/65',
};

// A single lead card. Carries its campaign color dot (so the legend is legible
// at a glance), the lvl2_id in the top-right corner, and a friendly
// "Mon, Jul 3" date for the next milestone. Qualified/Converted leads also show
// their AI-estimated opportunity value in the bottom-right corner.

// Company-logo resolution + the logo tile component live in ./LeadAvatar so the
// Kanban card and the Send-plan header share one implementation. See LeadAvatar.tsx.

const KanbanLeadCard: React.FC<{
  entity: UniBoxEntity;
  config: UniBoxConfig;
  groups: UniBoxGroup[];
  selected: boolean;
  onSelect: () => void;
  /** Team currency (ISO-4217) — used when the lead has no currency override. */
  currency?: string | null;
  showSubstatus?: boolean;
  density?: KanbanCardDensity;
}> = ({ entity, config, groups, selected, onSelect, currency, showSubstatus = true, density = 'detail' }) => {
  const prog = entity.sequenceProgress?.[0];
  const groupId = String(entity[config.group_by.key] || '');
  const colorIndex = getGroupColorIndex(groups, groupId);
  const groupName = groups.find((g) => g.id === groupId)?.name || groupId;
  const substatus = showSubstatus ? getKanbanCardSubstatus(config, entity) : null;
  // Prefer the enriched-lead id (lvl2_id) in the corner; fall back to lvl1_id.
  const idLabel = entity.lvl2_id ?? entity.lvl1_id;
  const dateRef = entity.next_send?.scheduled_at || entity.last_activity_at;
  // Opportunity value (Qualified/Converted only). Lead currency overrides team.
  const hasValue = entity.opportunity_value != null && entity.opportunity_value > 0;
  const valueCurrency = entity.opportunity_currency || currency || CURRENCY_FALLBACK;
  // Full hover tooltip — name, role, company, campaign. The card truncates these
  // for density; the title lets users read the full context on hover.
  const roleLabel = typeof entity.lead_role === 'string' ? entity.lead_role.trim() : '';
  const cardTooltip = [
    entity.client_name,
    roleLabel,
    entity.company,
    groupName !== groupId ? groupName : '',
  ].filter(Boolean).join(' · ');

  const statusDef = getStatusDefSafe(entity.status);
  // Company logo for the compact card's right-side slot. Null when no URL can be
  // resolved (card then renders without it).
  const companyLogoSrc = resolveCompanyLogoUrl(entity);
  const stagePill = showSubstatus ? (
    <span
      className={`inline-flex min-w-0 max-w-full cursor-pointer items-center gap-1 rounded-md border px-1.5 py-0.5 text-[9px] font-black uppercase tracking-[0.06em] backdrop-blur-sm transition-colors ${substatus ? (SUBSTATUS_GLASS[entity.status] || SUBSTATUS_GLASS.default) : (statusDef?.pill || SUBSTATUS_GLASS.default)}`}
      title={substatus ? `Substatus: ${substatus.label}${substatus.transitional ? ' — system processing' : ''}` : `Status: ${getStatusLabel(config, entity.status)}`}
    >
      {substatus?.transitional ? (
        // Transitional ("…") states render a spinner — the system is processing.
        <Icon icon="bx:loader-circle" className="h-3 w-3 shrink-0 animate-spin motion-reduce:animate-none opacity-80" />
      ) : (
        <Icon icon={substatus ? (substatus.icon || statusDef?.icon || 'bx:tag') : (statusDef?.icon || 'bx:tag')} className="h-3 w-3 shrink-0 opacity-80" />
      )}
      <span className="truncate">{substatus?.label || getStatusLabel(config, entity.status)}</span>
    </span>
  ) : null;

  const detailFooter = (
    <div className="flex min-w-0 items-center justify-between gap-1.5">
      <span className="inline-flex min-w-0 items-center gap-1 truncate text-[9px] font-medium text-white/45">
        <Icon icon="bx:calendar" className="h-3 w-3 shrink-0 text-white/35" />{formatWeekdayDate(dateRef)}
      </span>
      {prog && (
        <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[9px] font-semibold tabular-nums text-white/55">
          <Icon icon="bx:layer" className="h-3 w-3 text-white/35" />{prog.sent}/{prog.total}
        </span>
      )}
      <SequenceDots entity={entity} className="ml-auto" />
    </div>
  );

  const schema: KanbanCardSchema = {
    columns: 'minmax(0,7fr) minmax(76px,3fr)',
    left: [
      ...(stagePill ? [{
        key: 'substatus',
        content: stagePill,
        className: 'flex min-w-0',
      }] : []),
      {
        key: 'name',
        content: (
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <span className={`h-2 w-2 shrink-0 rounded-full ${GROUP_DOT[colorIndex % GROUP_DOT.length]}`} title={groupName} />
            <span className="truncate text-[11px] font-black leading-4 text-white">{entity.client_name}</span>
            {entity.control_state !== 'ai_running' && (
              <Icon
                icon={CONTROL_META[entity.control_state].icon}
                className={`h-3 w-3 shrink-0 ${entity.control_state === 'escalated' ? 'text-amber-300' : 'text-teal-300'}`}
              />
            )}
          </span>
        ),
        className: 'min-w-0',
      },
      {
        key: 'role',
        content: roleLabel || 'Role not enriched',
        className: `truncate text-[10px] font-semibold leading-4 ${roleLabel ? 'text-white/48' : 'text-white/22'}`,
        title: roleLabel,
      },
      {
        key: 'company',
        content: entity.company || entity.last_message_snippet,
        className: 'truncate text-[10px] font-bold leading-4 text-white/62',
        title: entity.company || entity.last_message_snippet,
      },
    ],
    right: [
      {
        key: 'id',
        content: `#${idLabel}`,
        className: 'shrink-0 whitespace-nowrap self-end rounded-md border border-white/8 bg-white/[0.035] px-1.5 py-0.5 text-[9px] font-black tabular-nums text-white/40',
      },
      {
        key: 'value',
        content: hasValue ? formatOpportunityValue(entity.opportunity_value as number, valueCurrency) : '',
        className: hasValue
          ? 'inline-flex shrink-0 items-center whitespace-nowrap self-end rounded-md border border-emerald-300/12 bg-emerald-400/[0.06] px-1.5 py-1 text-right text-[9px] font-black tabular-nums text-emerald-100/78'
          : 'h-[20px]',
      },
    ],
    footer: detailFooter,
    compact: {
      eyebrow: stagePill,
      title: entity.client_name,
      meta: entity.company || 'Company not enriched',
      aside: companyLogoSrc
        ? <CompanyLogoImg key={companyLogoSrc} src={companyLogoSrc} alt={entity.company || entity.client_name} className="h-8 w-8 p-1" />
        : undefined,
    },
  };

  return (
    <SchemaKanbanCard
      schema={schema}
      density={density}
      selected={selected}
      onClick={onSelect}
      title={cardTooltip}
    />
  );
};

// A selector row for ONE campaign inside the Lead Pool rail. Distinct from a
// lead card: it behaves like a campaign filter choice with campaign identity,
// status, and dense lead/enrichment metrics that remain legible in a narrow rail.
const KanbanCampaignCard: React.FC<{
  group: UniBoxGroup;
  colorIndex: number;
  totalLeads: number;
  enrichedLeads: number;
  selected: boolean;
  onSelect: () => void;
  onOpenDetails?: () => void;
  fillRow?: boolean;
}> = ({ group, colorIndex, totalLeads, enrichedLeads, selected, onSelect, onOpenDetails, fillRow = false }) => {
  const statusKey = group.status || 'active';
  const statusTheme = CAMPAIGN_STATUS_THEME[statusKey] || CAMPAIGN_STATUS_THEME.active;
  const enrichmentRate = totalLeads > 0 ? Math.round((enrichedLeads / totalLeads) * 100) : 0;
  // Target location comes from the campaign's search_location (the canonical
  // geo field on CampaignMetadata). Optional — real campaigns carry it, dummy
  // data may not, in which case the row is simply omitted.
  const rawLocation = typeof (group as { search_location?: unknown }).search_location === 'string'
    ? ((group as { search_location?: string }).search_location || '').trim()
    : '';
  const locationLabel = rawLocation || '';
  const sellerName = typeof group.seller_name === 'string' ? group.seller_name.trim() : '';
  // Active (selected) card uses a true WHITE background with BLACK text so every
  // label/value reads cleanly, and visually matches the Leads Intelligence
  // selected-card treatment. Secondary elements switch to dark-on-light variants.
  const selectedText = 'text-black';
  const selectedMutedText = 'text-black/55';
  // Status dot stays legible on white: use darker variants when selected.
  const CAMPAIGN_STATUS_DOT_DARK: Record<string, string> = {
    active: 'bg-cyan-700',
    paused: 'bg-amber-600',
    archived: 'bg-black/[0.45]',
  };
  const dotClassSelected = CAMPAIGN_STATUS_DOT_DARK[statusKey] || CAMPAIGN_STATUS_DOT_DARK.active;
  const statusLegend = `Campaign status: ${statusTheme.label}`;
  return (
    <div
      className={`group relative flex w-full cursor-pointer overflow-hidden rounded-lg border px-2.5 py-2 text-left transition-[background-color,border-color,box-shadow,opacity] focus-within:ring-1 focus-within:ring-white/55 ${fillRow ? 'h-full items-start' : 'items-center'} ${
        selected
          ? 'border-white bg-white shadow-[0_14px_30px_-22px_rgba(0,0,0,0.75),0_0_0_1px_rgba(255,255,255,0.28)]'
          : 'border-white/[0.075] bg-white/[0.028] shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] hover:border-white/[0.18] hover:bg-white/[0.065] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.055),0_10px_22px_-20px_rgba(255,255,255,0.28)]'
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        aria-label={`Filter by campaign ${group.name}`}
        title={`${group.name}${sellerName ? ` · Seller: ${sellerName}` : ''}${locationLabel ? ` · ${locationLabel}` : ''} · ${statusTheme.label} · ${totalLeads} leads · ${enrichedLeads} enriched`}
        className="absolute inset-0 z-0 rounded-lg focus-visible:outline-none"
      />
      <span className={`pointer-events-none absolute inset-y-2 left-0 w-0.5 rounded-r-full ${GROUP_DOT[colorIndex % GROUP_DOT.length]}`} aria-hidden="true" />
      <div className="pointer-events-none relative z-10 w-full min-w-0 pl-1">
        <div className="flex min-h-3 min-w-0 items-center justify-between gap-2">
          {sellerName ? (
            <span className={`flex min-w-0 items-center gap-1 text-[8px] font-black uppercase tracking-[0.1em] ${selected ? 'text-black/55' : 'text-white/42'}`}>
              <Icon icon="bx:user-circle" className="h-3 w-3 shrink-0" />
              <span className="truncate">{sellerName}</span>
            </span>
          ) : (
            <span />
          )}
          <span
            className={`inline-flex shrink-0 items-center gap-1 text-[8px] font-black uppercase tracking-[0.08em] ${selected ? 'text-black/52' : statusTheme.text}`}
            title={statusLegend}
            aria-label={statusLegend}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${selected ? dotClassSelected : statusTheme.dot}`} />
            {statusTheme.label}
          </span>
        </div>

        <div className="mt-1 flex min-w-0 items-start justify-between gap-2">
          <span className={`line-clamp-2 text-[12px] font-black leading-[1.2] ${selected ? selectedText : 'text-white/92'}`}>{group.name}</span>
          <span className="flex shrink-0 items-center gap-0.5">
            {onOpenDetails && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenDetails();
                }}
                className={`pointer-events-auto inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-1 ${
                  selected
                    ? 'text-black/48 hover:bg-black/[0.08] hover:text-black focus-visible:ring-black/35'
                    : 'text-white/42 hover:bg-white/[0.09] hover:text-white focus-visible:ring-white/45'
                }`}
                title="Campaign information"
                aria-label={`Open information for ${group.name}`}
              >
                <Icon icon="bx:info-circle" className="h-4 w-4" />
              </button>
            )}
            <Icon
              icon={selected ? 'bx:check' : 'bx:chevron-right'}
              className={`mt-px h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5 ${selected ? 'text-black/60' : 'text-white/38 group-hover:text-white/72'}`}
              aria-hidden="true"
            />
          </span>
        </div>

        {locationLabel && (
          <div className={`mt-1 flex items-center gap-1 text-[9px] font-semibold ${selected ? 'text-black/52' : 'text-white/38'}`}>
            <Icon icon="bx:map" className={`h-3 w-3 shrink-0 ${selected ? 'text-black/42' : 'text-white/28'}`} />
            <span className="truncate">{locationLabel}</span>
          </div>
        )}

        <div className={`mt-2 border-t pt-1.5 ${selected ? 'border-black/[0.09]' : 'border-white/[0.075]'}`}>
          <div className="grid grid-cols-2 gap-2">
            <span className="min-w-0">
              <span className={`block truncate text-[16px] font-black leading-none tabular-nums ${selected ? selectedText : 'text-white/92'}`}>{totalLeads.toLocaleString()}</span>
              <span className={`mt-0.5 block text-[8px] font-black uppercase tracking-[0.1em] ${selected ? selectedMutedText : 'text-white/36'}`}>Leads</span>
            </span>
            <span className={`min-w-0 border-l pl-2 ${selected ? 'border-black/[0.09]' : 'border-white/[0.075]'}`}>
              <span className={`block truncate text-[16px] font-black leading-none tabular-nums ${selected ? selectedText : 'text-white/92'}`}>{enrichedLeads.toLocaleString()}</span>
              <span className={`mt-0.5 block text-[8px] font-black uppercase tracking-[0.1em] ${selected ? selectedMutedText : 'text-white/36'}`}>Enriched</span>
            </span>
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <span className={`h-1 min-w-0 flex-1 overflow-hidden rounded-full ${selected ? 'bg-black/[0.1]' : 'bg-white/[0.09]'}`} aria-hidden="true">
              <span
                className={`block h-full rounded-full ${selected ? 'bg-black/70' : 'bg-cyan-300/75'}`}
                style={{ width: `${Math.min(100, Math.max(0, enrichmentRate))}%` }}
              />
            </span>
            <span className={`shrink-0 text-[10px] font-black tabular-nums ${selected ? 'text-black/66' : 'text-white/58'}`}>{enrichmentRate}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

type KanbanSubstatusDef = { key: string; label: string; icon?: string };
type KanbanSubstatusBucket = KanbanSubstatusDef & { items: UniBoxEntity[]; sequence: number };

const resolveKanbanSubstatusKey = (entity: UniBoxEntity, statusKey: string): string => {
  const substatus = typeof entity.substatus === 'string' ? entity.substatus.trim() : '';
  if (substatus) return substatus;

  const outcome = typeof entity.outcome === 'string' ? entity.outcome.trim() : '';
  if (statusKey === 'discarded' && outcome) return outcome;

  return OTHER_SUBSTATUS_KEY;
};

const buildKanbanSubstatusBuckets = (
  items: UniBoxEntity[],
  substatuses: KanbanSubstatusDef[],
  statusKey: string,
): KanbanSubstatusBucket[] => {
  const known = new Map<string, UniBoxEntity[]>();
  substatuses.forEach((substatus) => known.set(substatus.key, []));
  const extras = new Map<string, UniBoxEntity[]>();

  items.forEach((entity) => {
    const key = resolveKanbanSubstatusKey(entity, statusKey);
    if (known.has(key)) {
      known.get(key)!.push(entity);
      return;
    }
    extras.set(key, [...(extras.get(key) || []), entity]);
  });

  const buckets: KanbanSubstatusBucket[] = substatuses.map((substatus, index) => ({
    ...substatus,
    sequence: index + 1,
    items: known.get(substatus.key) || [],
  }));

  let extraIndex = substatuses.length;
  extras.forEach((extraItems, key) => {
    extraIndex += 1;
    buckets.push({
      key,
      label: key === OTHER_SUBSTATUS_KEY ? 'Other' : formatLabel(key),
      sequence: extraIndex,
      items: extraItems,
    });
  });

  return buckets;
};

const KanbanStatusHeader: React.FC<{
  label: string;
  count: number;
  def: ReturnType<typeof getStatusDefSafe>;
  metric?: React.ReactNode;
  action?: React.ReactNode;
  muted?: boolean;
  progress?: number;
}> = ({ label, count, def, metric, action, muted, progress }) => (
  <div
    className={`mb-2 flex h-[58px] flex-col justify-between rounded-lg border px-2.5 py-1.5 ${def?.accentColor ? 'bg-black/25' : muted ? 'border-white/[0.045] bg-black/[0.15]' : 'border-white/[0.055] bg-black/25'}`}
    style={getKanbanHeaderStyle(def?.accentColor)}
  >
    {/* Row 1 — status title + total item count (the most relevant info, never cropped). */}
    <div className="flex items-center justify-between gap-2">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="h-2 w-2 shrink-0 rounded-full bg-white/90 shadow-[0_0_10px_rgba(255,255,255,0.45)]" />
        <span className="truncate text-[11px] font-black uppercase tracking-[0.08em] text-white">
          {label}
        </span>
      </span>
      <span className="tabular-nums text-[13px] font-black text-white">{count}</span>
    </div>
    {/* Row 2 — progress bar (left) + opportunity total + expand/collapse action.
        Always rendered (even when empty) so every column header is the same
        height and the board reads as a balanced grid. Empty slots reserve
        their footprint invisibly. */}
    <div className="flex items-center justify-between gap-2">
      <span className="flex min-w-0 items-center gap-1.5">
        {progress !== undefined ? (
          <span className="h-1 w-10 shrink-0 overflow-hidden rounded-full bg-white/20" title={`${progress}% of active pipeline`}>
            <span className="block h-full rounded-full bg-white/90" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
          </span>
        ) : (
          <span className="inline-block h-1 w-10 shrink-0 rounded-full bg-white/20 opacity-0" aria-hidden="true" />
        )}
        {metric || <span className="inline-block h-[10px] w-[44px]" aria-hidden="true" />}
      </span>
      {action || <span className="inline-block h-[18px] w-[18px]" aria-hidden="true" />}
    </div>
  </div>
);

const CampaignSelectorHeader: React.FC<{
  campaignCount: number;
  selected: boolean;
  onSelect: () => void;
}> = ({ campaignCount, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    aria-pressed={selected}
    title="Show all campaigns"
    className={`mb-2 grid h-[58px] w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded-lg border px-2.5 py-1.5 text-left transition-[background-color,border-color,box-shadow,transform] active:translate-y-px focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/55 ${
      selected
        ? 'border-white bg-white shadow-[0_12px_24px_-20px_rgba(0,0,0,0.72)]'
        : 'border-white/[0.08] bg-white/[0.035] hover:border-white/[0.18] hover:bg-white/[0.07]'
    }`}
  >
    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border ${selected ? 'border-black/[0.08] bg-black text-white' : 'border-white/[0.08] bg-white/[0.055] text-white/70'}`}>
      <Icon icon="bx:filter-alt" className="h-4 w-4" />
    </span>
    <span className="min-w-0">
      <span className={`block text-[8px] font-black uppercase tracking-[0.14em] ${selected ? 'text-black/48' : 'text-white/38'}`}>Campaign filter</span>
      <span className={`block truncate text-[11px] font-black uppercase tracking-[0.08em] ${selected ? 'text-black' : 'text-white/92'}`}>All campaigns</span>
    </span>
    <span className="flex flex-col items-end">
      <span className={`text-[16px] font-black leading-none tabular-nums ${selected ? 'text-black' : 'text-white/92'}`}>{campaignCount}</span>
      <span className={`mt-0.5 text-[7px] font-black uppercase tracking-[0.1em] ${selected ? 'text-black/45' : 'text-white/34'}`}>Campaigns</span>
    </span>
  </button>
);

const KanbanStatusView: React.FC<{
  config: UniBoxConfig;
  entities: UniBoxEntity[];
  groups: UniBoxGroup[];
  visibleStatuses: Set<string>;
  selectedEntityId: number | null;
  onSelect: (e: UniBoxEntity) => void;
  groupBy?: GroupByKey;
  cardDensity?: KanbanCardDensity;
  focusedGroupId: string | null;
  onFocusedGroupChange: (id: string | null) => void;
  onOpenCampaignDetails?: (groupId: string) => void;
  /** Team currency (ISO-4217) for opportunity totals + per-card values. */
  currency?: string | null;
}> = ({ config, entities, groups, visibleStatuses, selectedEntityId, onSelect, groupBy = 'campaign', cardDensity = 'detail', focusedGroupId, onFocusedGroupChange, onOpenCampaignDetails, currency }) => {
  const useDocumentScroll = config.product === 'sales_intelligence';
  const [pageByCol, setPageByCol] = useState<Record<string, number>>({});
  const [groupCollapseState, setGroupCollapseState] = useState<{
    groupBy: GroupByKey;
    overrides: Record<string, boolean>;
  }>(() => ({ groupBy, overrides: {} }));
  const collapsedGroups = groupCollapseState.groupBy === groupBy ? groupCollapseState.overrides : {};
  // Engaged stays open by default because it is the negotiation/control-center stage.
  const [expandedByStatus, setExpandedByStatus] = useState<Record<string, boolean>>({ engaged: true });
  const toggleCollapsedGroup = useCallback((groupKey: string, defaultCollapsed: boolean) => {
    setGroupCollapseState((current) => {
      const overrides = current.groupBy === groupBy ? current.overrides : {};
      const isCollapsed = overrides[groupKey] ?? defaultCollapsed;
      return {
        groupBy,
        overrides: { ...overrides, [groupKey]: !isCollapsed },
      };
    });
  }, [groupBy]);
  const toggleExpandedStatus = useCallback((statusKey: string) => {
    setExpandedByStatus((current) => ({ ...current, [statusKey]: !current[statusKey] }));
  }, []);
  // Archived/completed campaigns are hidden by default — the board is for
  // commanding CURRENT campaigns. Toggling reveals them in the Lead Pool.
  const [showArchived, setShowArchived] = useState(false);

  // Apply the campaign focus: null = All campaigns, else isolate one campaign
  // across every status column (the "see how this campaign flows" read).
  const allCampaignMatrixMode = focusedGroupId === null;
  const allCampaignSummaryMode = allCampaignMatrixMode && groupBy === 'campaign';
  const selectedCampaignDetailMode = focusedGroupId !== null && groupBy === 'campaign';
  const boardEntities = useMemo(
    () => (focusedGroupId ? entities.filter((e) => String(e[config.group_by.key] || '') === focusedGroupId) : entities),
    [entities, focusedGroupId, config.group_by.key],
  );

  // Per-campaign Lead Pool stats — Total Leads (raw + enriched) and Total
  // Enriched — computed from the FULL entity set (not the focused subset) so
  // the campaign cards stay accurate while a single campaign is isolated.
  // Archived/completed campaigns are filtered out unless `showArchived` is on.
  const campaignPoolStats = useMemo(() => {
    const map = new Map<string, { total: number; enriched: number }>();
    entities.forEach((e) => {
      const gk = String(e[config.group_by.key] || '');
      if (!gk) return;
      const entry = map.get(gk) || { total: 0, enriched: 0 };
      if (e.status === 'leads' || e.status === 'enriched') entry.total += 1;
      if (e.status === 'enriched') entry.enriched += 1;
      map.set(gk, entry);
    });
    return map;
  }, [entities, config.group_by.key]);

  const visibleCampaignGroups = useMemo(() => {
    return groups.filter((g) => {
      const status = g.status || 'active';
      const isArchived = status === 'archived';
      return showArchived ? true : !isArchived;
    });
  }, [groups, showArchived]);
  const hiddenArchivedCount = groups.length - visibleCampaignGroups.length;

  // Lead Pool fuses the raw `leads` and `enriched` stages into one column so
  // the pipeline reads as a single sourcing stage (Lead Pool → Outreaching →
  // Engaged → Proposal → Converted → Discarded). The column body is a list of CAMPAIGN
  // summary cards (Total Leads / Total Enriched) — not lead cards — and doubles
  // as the board's campaign filter. `items` carries the enriched leads so the
  // active-pipeline progress math still works.
  const LEAD_POOL_KEY = 'lead_pool';
  const allColumns = useMemo(() => {
    const funnel = config.funnelColumns.slice();
    const leadIdx = funnel.indexOf('leads');
    const enrichedIdx = funnel.indexOf('enriched');
    const hasFusion = leadIdx !== -1 && enrichedIdx !== -1;
    if (!hasFusion) {
      return funnel.map((key) => ({
        key,
        def: getStatusDefSafe(key),
        items: boardEntities.filter((e) => e.status === key),
      }));
    }
    const enrichedDef = getStatusDefSafe('enriched');
    const enrichedLeads = boardEntities.filter((e) => e.status === 'enriched');
    // Drop both stages from the funnel, then splice the fused column in at the
    // earliest of the two positions so it stays at the top of the board.
    const remaining = funnel.filter((k) => k !== 'leads' && k !== 'enriched');
    const insertAt = Math.min(leadIdx, enrichedIdx);
    const fused = {
      key: LEAD_POOL_KEY,
      def: enrichedDef,
      items: enrichedLeads,
    };
    const rebuilt = [...remaining.slice(0, insertAt), LEAD_POOL_KEY, ...remaining.slice(insertAt)];
    return rebuilt.map((key) => {
      if (key === LEAD_POOL_KEY) return fused;
      return {
        key,
        def: getStatusDefSafe(key),
        items: boardEntities.filter((e) => e.status === key),
      };
    });
  }, [config.funnelColumns, boardEntities]);
  const columns = useMemo(
    () =>
      allColumns.filter((col) => {
        if (visibleStatuses.size === 0) return true;
        if (col.key === LEAD_POOL_KEY) {
          // Lead Pool is shown when the fused option or either raw stage is selected.
          return visibleStatuses.has('lead_pool') || visibleStatuses.has('leads') || visibleStatuses.has('enriched');
        }
        return visibleStatuses.has(col.key);
      }),
    [allColumns, visibleStatuses, LEAD_POOL_KEY],
  );

  const totalLeads = boardEntities.length;
  // Discarded is the only quiet sink. Converted stays in the visible pipeline
  // distribution so its header progress bar remains visible.
  const isSink = (key: string) => key === 'discarded';

  // Number of *visible* (non-empty) sub-status lanes for each status column when
  // expanded. Computed up front so the grid template can size each column
  // proportionally to how many lanes it actually renders (1, 2, 3, …).
  const visibleLaneCountByStatus = useMemo(() => {
    const map: Record<string, number> = {};
    columns.forEach((col) => {
      const substatusDefs = config.substatusByStage[col.key] || [];
      const hasSubstatusLanes = groupBy !== 'substatus' && substatusDefs.length > 0;
      if (!hasSubstatusLanes) return;
      const buckets = buildKanbanSubstatusBuckets(col.items, substatusDefs, col.key).filter((b) => b.items.length > 0);
      map[col.key] = buckets.length;
    });
    return map;
  }, [columns, config.substatusByStage, groupBy]);

  const boardGridTemplate = useMemo(
    () =>
      columns.map((col) => {
        if (col.key === LEAD_POOL_KEY) return 'minmax(0,1fr)';
        const hasSubstatusLanes = groupBy !== 'substatus' && (config.substatusByStage[col.key] || []).length > 0;
        const expandedForLayout = hasSubstatusLanes && !allCampaignMatrixMode && expandedByStatus[col.key];
        if (expandedForLayout && col.items.length > 0) {
          // A status column grows to fit its visible sub-status lanes so the
          // lanes never overflow into horizontal scrolling. Each lane ~= 1 unit;
          // add a small base so a single lane isn't absurdly narrow.
          const lanes = Math.max(1, visibleLaneCountByStatus[col.key] || 1);
          return `minmax(0,${(0.85 + lanes).toFixed(2)}fr)`;
        }
        if (hasSubstatusLanes) return 'minmax(0,1.05fr)';
        return 'minmax(0,1fr)';
      }).join(' '),
    [columns, config.substatusByStage, expandedByStatus, groupBy, visibleLaneCountByStatus, allCampaignMatrixMode, LEAD_POOL_KEY],
  );
  const activeColumns = columns.filter((col) => !isSink(col.key));
  const activePipelineTotal = activeColumns.reduce((sum, col) => sum + col.items.length, 0) || 1;

  // Index once so the All Campaigns matrix can place each existing campaign
  // card in its status/campaign coordinate without repeatedly scanning leads.
  const campaignItemsByColumn = useMemo(() => {
    const byColumn = new Map<string, Map<string, UniBoxEntity[]>>();
    columns.forEach((col) => {
      const byCampaign = new Map<string, UniBoxEntity[]>();
      col.items.forEach((entity) => {
        const campaignId = String(entity[config.group_by.key] || '');
        if (!campaignId) return;
        const campaignItems = byCampaign.get(campaignId) || [];
        campaignItems.push(entity);
        byCampaign.set(campaignId, campaignItems);
      });
      byColumn.set(col.key, byCampaign);
    });
    return byColumn;
  }, [columns, config.group_by.key]);

  // Campaign remains the primary row axis in every All Campaigns view. The
  // selected Group By becomes a secondary row axis inside each campaign, with
  // one ordered union shared by every status column for that campaign.
  const campaignMatrixSections = useMemo(() => visibleCampaignGroups.map((campaign) => {
    const byColumn = new Map<string, Map<string, KanbanGroupBucket>>();
    if (groupBy === 'campaign') {
      return {
        campaign,
        rows: [{ key: `cmp:${campaign.id}`, label: campaign.name, items: [] }] as KanbanGroupBucket[],
        byColumn,
      };
    }

    const rowsByKey = new Map<string, KanbanGroupBucket>();
    columns.forEach((col) => {
      if (col.key === LEAD_POOL_KEY) return;

      const campaignItems = campaignItemsByColumn.get(col.key)?.get(campaign.id) || [];
      const buckets = bucketColumnItems(campaignItems, groupBy, config, groups);
      if (groupBy === 'substatus') {
        const order = new Map(
          (config.substatusByStage[col.key] || []).map((substatus, index) => [
            `sub:${col.key}:${substatus.key}`,
            index,
          ]),
        );
        buckets.sort((a, b) => (order.get(a.key) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.key) ?? Number.MAX_SAFE_INTEGER));
      }

      const columnBuckets = new Map<string, KanbanGroupBucket>();
      buckets.forEach((bucket) => {
        columnBuckets.set(bucket.key, bucket);
        if (!rowsByKey.has(bucket.key)) rowsByKey.set(bucket.key, { ...bucket, items: [] });
      });
      byColumn.set(col.key, columnBuckets);
    });

    const rows = Array.from(rowsByKey.values());
    if (groupBy === 'week' || groupBy === 'day') {
      rows.sort((a, b) => a.key.localeCompare(b.key));
    } else if (groupBy === 'channel') {
      const order = new Map(config.channels.map((channel, index) => [`ch:${channel}`, index]));
      rows.sort((a, b) => {
        const byConfig = (order.get(a.key) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.key) ?? Number.MAX_SAFE_INTEGER);
        return byConfig || a.label.localeCompare(b.label);
      });
    }

    if (rows.length === 0) {
      rows.push({ key: `empty:${campaign.id}`, label: campaign.name, items: [] });
    }
    return { campaign, rows, byColumn };
  }), [campaignItemsByColumn, columns, config, groupBy, groups, LEAD_POOL_KEY, visibleCampaignGroups]);

  if (totalLeads === 0) return <EmptyState label="No leads match these filters" icon="bx:columns" />;

  // Sum the opportunity value of a column's Qualified/Converted leads into a
  // SINGLE total (in the team currency) — the header shows one number, not a
  // per-currency breakdown. Leads carrying a currency override are summed at
  // their numeric value (the override is rare and the total is indicative).
  const renderColumnTotal = (items: UniBoxEntity[]) => {
    let total = 0;
    let hasAny = false;
    items.forEach((e) => {
      if (e.opportunity_value == null || e.opportunity_value <= 0) return;
      total += e.opportunity_value;
      hasAny = true;
    });
    if (!hasAny) return null;
    const code = (currency || CURRENCY_FALLBACK).toUpperCase();
    return (
      <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold tabular-nums text-white/85" title="Total opportunity value in this column">
        <Icon icon="bx:coin" className="h-2.5 w-2.5 text-white/70" />{formatOpportunityValue(total, code)}
      </span>
    );
  };

  // Render a column's visible cards, sub-bucketed by the active group-by
  // dimension (Campaign / Week / Day / Channel / Substatus). Shared by every card-list
  // column — Lead Pool, the substatus-lane collapsed view, and the default
  // columns — so grouping is consistent everywhere, not just the default path.
  const renderLeadCards = (items: UniBoxEntity[], showSubstatus = true) => sortKanbanCardsByDate(items).map((e) => (
    <KanbanLeadCard
      key={e.lvl1_id}
      entity={e}
      config={config}
      groups={groups}
      selected={selectedEntityId === e.lvl1_id}
      onSelect={() => onSelect(e)}
      currency={currency}
      showSubstatus={showSubstatus}
      density={cardDensity}
    />
  ));

  const renderCampaignSubstatusSummary = (items: UniBoxEntity[], statusKey: string) => {
    const substatuses = config.substatusByStage[statusKey] || [];
    if (substatuses.length === 0) return null;

    const counts = new Map<string, number>();
    items.forEach((entity) => {
      const key = resolveKanbanSubstatusKey(entity, statusKey);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const knownKeys = new Set(substatuses.map((item) => item.key));
    const extraItems = Array.from(counts.entries())
      .filter(([key]) => !knownKeys.has(key))
      .map(([key, count]) => ({ key, label: key === OTHER_SUBSTATUS_KEY ? 'Other' : formatLabel(key), count }));
    const summaryItems = [
      ...substatuses.map((item) => ({ key: item.key, label: item.label, icon: item.icon, count: counts.get(item.key) || 0 })),
      ...extraItems,
    ];

    return (
      <span className="grid w-full min-w-0 grid-cols-1 gap-0.5">
        {summaryItems.map((item) => (
          <span
            key={item.key}
            className={`grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-md px-1.5 py-0.5 text-[8px] font-black uppercase tracking-[0.08em] ${
              item.count > 0
                ? 'bg-black/[0.22] text-white/72'
                : 'bg-black/10 text-white/28'
            }`}
            title={`${item.label}: ${item.count}`}
          >
            <span className="inline-flex min-w-0 items-center gap-1">
              {'icon' in item && item.icon && <Icon icon={item.icon} className="h-3 w-3 shrink-0 opacity-70" />}
              <span className="truncate">{item.label}</span>
            </span>
            <span className={`min-w-[1.25rem] text-right tabular-nums ${item.count > 0 ? 'text-white/90' : 'text-white/35'}`}>{item.count}</span>
          </span>
        ))}
      </span>
    );
  };

  const renderGroupBucket = (
    bucket: KanbanGroupBucket,
    showSubstatus = true,
    columnKey = 'board',
    collapseKey?: string,
  ) => {
    const groupKey = collapseKey || `${columnKey}:${bucket.key}`;
    const summaryOnly = allCampaignSummaryMode && bucket.key.startsWith('cmp:');
    const forceOpen = selectedCampaignDetailMode && bucket.key.startsWith('cmp:');
    const defaultCollapsed = groupBy !== 'campaign';
    const collapsed = summaryOnly || (!forceOpen && (collapsedGroups[groupKey] ?? defaultCollapsed));
    const statusKey = columnKey.split(':')[0];
    const statusAccentColor = getStatusDefSafe(statusKey)?.accentColor;
    const groupHeaderStyle = groupBy === 'campaign'
      ? getGroupSummaryHeaderStyle(bucket.colorIndex)
      : getStatusGroupHeaderStyle(statusAccentColor);
    const groupHeaderClassName = summaryOnly
      ? 'border-white/[0.055] bg-white/[0.045] px-2.5 py-2'
      : 'border-white/[0.055] bg-white/[0.035] px-2.5 py-1.5';
    const groupCountClassName = summaryOnly
      ? '!border-0 min-w-7 px-2.5 py-1 text-center text-[11px] !text-white'
      : '!border-0 min-w-6 px-2 text-center text-[10px] !text-white';
    const groupCountAccent = summaryOnly || groupBy !== 'campaign'
      ? statusAccentColor
      : GROUP_HEX[(bucket.colorIndex ?? 0) % GROUP_HEX.length];
    const groupCountStyle: React.CSSProperties = {
      background: groupCountAccent
        ? `linear-gradient(180deg, ${hexToRgba(groupCountAccent, 0.32)}, ${hexToRgba(groupCountAccent, 0.22)})`
        : 'rgba(255,255,255,0.16)',
      boxShadow: groupCountAccent
        ? `inset 0 1px 0 rgba(255,255,255,0.07), 0 3px 10px -7px ${hexToRgba(groupCountAccent, 0.8)}`
        : 'inset 0 1px 0 rgba(255,255,255,0.06)',
    };
    const showCardSubstatus = showSubstatus && groupBy !== 'substatus' && groupBy !== 'week' && groupBy !== 'day' && groupBy !== 'channel';

    return (
      <div key={bucket.key} className="space-y-1.5">
        <KanbanGroupHeader
          label={bucket.label}
          count={bucket.items.length}
          icon={bucket.icon}
          dotClassName={bucket.dotClassName}
          toneClassName={bucket.toneClassName}
          meta={summaryOnly ? renderCampaignSubstatusSummary(bucket.items, columnKey) : undefined}
          className={groupHeaderClassName}
          countClassName={groupCountClassName}
          countStyle={groupCountStyle}
          style={groupHeaderStyle}
          collapsed={collapsed}
          onToggle={summaryOnly || forceOpen ? undefined : () => toggleCollapsedGroup(groupKey, defaultCollapsed)}
        />
        {!collapsed && (
          <div className="space-y-1">
            {renderLeadCards(bucket.items, showCardSubstatus)}
          </div>
        )}
      </div>
    );
  };

  const renderGroupedCards = (items: UniBoxEntity[], showSubstatus = true, columnKey = 'board') =>
    bucketColumnItems(items, groupBy, config, groups).map((bucket) => renderGroupBucket(bucket, showSubstatus, columnKey));

  const renderArchivedCampaignsControl = () => (
    <button
      type="button"
      onClick={() => setShowArchived((value) => !value)}
      aria-pressed={showArchived}
      className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-black/[0.18] px-2 py-1.5 text-[9px] font-black uppercase tracking-[0.12em] text-white/40 transition-colors hover:bg-white/[0.05] hover:text-white/70"
    >
      <Icon icon={showArchived ? 'bx:hide' : 'bx:show'} className="h-3 w-3" />
      {showArchived ? 'Hide' : 'Show'} archived · {hiddenArchivedCount}
    </button>
  );

  // All Campaigns is always a campaign-first matrix. Campaign grouping uses
  // one row per campaign; every other Group By adds synchronized secondary
  // rows inside that campaign while preserving the existing card renderers.
  const renderCampaignMatrix = () => {
    const hasArchivedControl = hiddenArchivedCount > 0;
    const hasEmptyCampaignRow = campaignMatrixSections.length === 0;
    const campaignRowCount = campaignMatrixSections.reduce((total, section) => total + section.rows.length, 0);
    const matrixRowCount = 1 + campaignRowCount + (hasEmptyCampaignRow ? 1 : 0) + (hasArchivedControl ? 1 : 0);
    let campaignGridRow = 1;
    const campaignSeparatorRows = campaignMatrixSections.map((section) => {
      campaignGridRow += section.rows.length;
      return { key: section.campaign.id, row: campaignGridRow };
    }).slice(0, -1);

    return (
      <div className={useDocumentScroll ? 'overflow-x-auto pb-1' : 'min-h-0 flex-1 overflow-auto pb-1'}>
        <div
          className="relative isolate grid min-w-0 gap-x-2"
          style={{
            gridTemplateColumns: boardGridTemplate,
            gridTemplateRows: `repeat(${matrixRowCount}, max-content)`,
          }}
        >
          {columns.map((col, columnIndex) => {
            const sink = isSink(col.key);
            const progress = sink ? undefined : Math.round((col.items.length / activePipelineTotal) * 100);
            const isLeadPool = col.key === LEAD_POOL_KEY;
            const columnClassName = isLeadPool
              ? 'border-white/[0.075] bg-[#15161a] shadow-[inset_-1px_0_0_rgba(255,255,255,0.045),inset_0_1px_0_rgba(255,255,255,0.035)] ring-1 ring-inset ring-white/[0.02]'
              : `shadow-[inset_0_1px_0_rgba(255,255,255,0.025)] ${sink ? 'border-white/[0.04] bg-black/[0.28]' : 'border-white/[0.045] bg-black/[0.22]'}`;

            return (
              <section
                key={col.key}
                className={`grid min-w-0 overflow-hidden rounded-xl border p-2 ${columnClassName}`}
                style={{
                  gridColumn: columnIndex + 1,
                  gridRow: `1 / span ${matrixRowCount}`,
                  gridTemplateRows: 'subgrid',
                }}
              >
                {isLeadPool ? (
                  <CampaignSelectorHeader
                    campaignCount={visibleCampaignGroups.length}
                    selected={focusedGroupId === null}
                    onSelect={() => onFocusedGroupChange(null)}
                  />
                ) : (
                  <KanbanStatusHeader
                    def={col.def}
                    label={col.def?.label || formatLabel(col.key)}
                    count={col.items.length}
                    metric={renderColumnTotal(col.items)}
                    progress={progress}
                    muted={sink}
                  />
                )}

                {campaignMatrixSections.map((section, sectionIndex) => {
                  const campaignItems = campaignItemsByColumn.get(col.key)?.get(section.campaign.id) || [];
                  const stats = campaignPoolStats.get(section.campaign.id) || { total: 0, enriched: 0 };

                  if (isLeadPool) {
                    return (
                      <div
                        key={section.campaign.id}
                        className={`flex min-w-0 items-stretch pb-3 ${sectionIndex > 0 ? 'pt-3' : ''}`}
                        style={{ gridRow: `span ${section.rows.length}` }}
                      >
                        <KanbanCampaignCard
                          group={section.campaign}
                          colorIndex={getGroupColorIndex(groups, section.campaign.id)}
                          totalLeads={stats.total}
                          enrichedLeads={stats.enriched}
                          selected={false}
                          onSelect={() => onFocusedGroupChange(section.campaign.id)}
                          onOpenDetails={onOpenCampaignDetails ? () => onOpenCampaignDetails(section.campaign.id) : undefined}
                          fillRow
                        />
                      </div>
                    );
                  }

                  return section.rows.map((row, rowIndex) => {
                    const bucket = section.byColumn.get(col.key)?.get(row.key);
                    return (
                      <div
                        key={`${section.campaign.id}:${row.key}`}
                        className={`min-w-0 pb-3 ${sectionIndex > 0 && rowIndex === 0 ? 'pt-3' : ''}`}
                      >
                        {groupBy === 'campaign'
                          ? rowIndex === 0 && campaignItems.length > 0
                            ? renderGroupedCards(campaignItems, true, col.key)
                            : null
                          : bucket
                            ? renderGroupBucket(
                              bucket,
                              true,
                              col.key,
                              `matrix:${section.campaign.id}:${col.key}:${groupBy}:${row.key}`,
                            )
                            : null}
                      </div>
                    );
                  });
                })}

                {hasEmptyCampaignRow && (
                  <div className="min-w-0">
                    {isLeadPool ? (
                      <div className="px-2 py-6 text-center text-[11px] font-semibold text-white/20">
                        {showArchived ? 'No campaigns' : 'No active campaigns'}
                      </div>
                    ) : null}
                  </div>
                )}

                {hasArchivedControl && (
                  <div className="min-w-0">
                    {isLeadPool ? renderArchivedCampaignsControl() : null}
                  </div>
                )}
              </section>
            );
          })}
          {campaignSeparatorRows.map((separator) => (
            <span
              key={`campaign-separator:${separator.key}`}
              aria-hidden="true"
              className="pointer-events-none relative z-20 h-px self-end bg-[#3b3d43] shadow-[0_1px_0_rgba(0,0,0,0.32)]"
              style={{ gridColumn: '2 / -1', gridRow: separator.row }}
            />
          ))}
        </div>
      </div>
    );
  };

  if (allCampaignMatrixMode) {
    return <div className={`flex flex-col ${useDocumentScroll ? '' : 'h-full'}`}>{renderCampaignMatrix()}</div>;
  }

  return (
    <div className={`flex flex-col ${useDocumentScroll ? '' : 'h-full'}`}>
      {/* The Lead Pool column IS the campaign selector now (its campaign cards
          focus the board on a campaign). No separate focus strip on top. */}

      <div
        className={useDocumentScroll ? 'grid flex-1 gap-2 overflow-x-auto pb-1' : 'grid min-h-0 flex-1 gap-2 overflow-hidden pb-1'}
        style={{ gridTemplateColumns: boardGridTemplate }}
      >
        {columns.map((col) => {
          const sink = isSink(col.key);
          const limit = pageByCol[col.key] || KANBAN_PAGE_SIZE;
          const progress = sink ? undefined : Math.round((col.items.length / activePipelineTotal) * 100);
          const substatusDefs = config.substatusByStage[col.key] || [];
          const hasSubstatusLanes = groupBy !== 'substatus' && substatusDefs.length > 0;
          const substatusBuckets = hasSubstatusLanes ? buildKanbanSubstatusBuckets(col.items, substatusDefs, col.key) : [];
          const visibleSubstatusBuckets = substatusBuckets.filter((bucket) => bucket.items.length > 0);
          const orderedItems = hasSubstatusLanes && visibleSubstatusBuckets.length > 0
            ? visibleSubstatusBuckets.flatMap((bucket) => bucket.items)
            : col.items;
          const shown = allCampaignSummaryMode ? orderedItems : orderedItems.slice(0, limit);
          const hidden = allCampaignSummaryMode ? 0 : orderedItems.length - shown.length;

          // LEAD POOL → a CAMPAIGN SELECTOR rail, not a pipeline stage. Campaign
          // cards isolate one campaign across every status column, and the "All"
          // control restores the full board. Keep its visual language neutral so
          // it does not compete with the color-coded Kanban status columns.
          if (col.key === LEAD_POOL_KEY) {
            return (
              <SchemaKanbanColumn
                key={col.key}
                className="!border-white/[0.075] bg-[#15161a] shadow-[inset_-1px_0_0_rgba(255,255,255,0.045),inset_0_1px_0_rgba(255,255,255,0.035)] ring-1 ring-inset ring-white/[0.02]"
                bodyClassName={useDocumentScroll ? 'space-y-2 pr-0.5' : 'min-h-0 flex-1 space-y-2 overflow-y-auto pr-0.5'}
                verticalScroll={useDocumentScroll ? 'page' : 'column'}
                header={(
                  <CampaignSelectorHeader
                    campaignCount={visibleCampaignGroups.length}
                    selected={focusedGroupId === null}
                    onSelect={() => onFocusedGroupChange(null)}
                  />
                )}
              >
                {visibleCampaignGroups.map((g) => {
                  const stats = campaignPoolStats.get(g.id) || { total: 0, enriched: 0 };
                  return (
                    <KanbanCampaignCard
                      key={g.id}
                      group={g}
                      colorIndex={getGroupColorIndex(groups, g.id)}
                      totalLeads={stats.total}
                      enrichedLeads={stats.enriched}
                      selected={focusedGroupId === g.id}
                      onSelect={() => onFocusedGroupChange(focusedGroupId === g.id ? null : g.id)}
                      onOpenDetails={onOpenCampaignDetails ? () => onOpenCampaignDetails(g.id) : undefined}
                    />
                  );
                })}
                {visibleCampaignGroups.length === 0 && (
                  <div className="px-2 py-6 text-center text-[11px] font-semibold text-white/20">
                    {showArchived ? 'No campaigns' : 'No active campaigns'}
                  </div>
                )}
                {/* Archived toggle — reveal/hide completed/archived campaigns. */}
                {hiddenArchivedCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowArchived((v) => !v)}
                    aria-pressed={showArchived}
                    className="mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-black/[0.18] px-2 py-1.5 text-[9px] font-black uppercase tracking-[0.12em] text-white/40 transition-colors hover:bg-white/[0.05] hover:text-white/70"
                  >
                    <Icon icon={showArchived ? 'bx:hide' : 'bx:show'} className="h-3 w-3" />
                    {showArchived ? 'Hide' : 'Show'} archived · {hiddenArchivedCount}
                  </button>
                )}
              </SchemaKanbanColumn>
            );
          }

          // Statuses with substatus vocabulary can expand into internal lanes.
          if (hasSubstatusLanes) {
            const expanded = !allCampaignSummaryMode && !!expandedByStatus[col.key];
            return (
              <SchemaKanbanColumn
                key={col.key}
                className={`shadow-[inset_0_1px_0_rgba(255,255,255,0.025)] ${sink ? 'border-white/[0.04] bg-black/[0.28]' : 'border-white/[0.045] bg-black/[0.22]'}`}
                bodyClassName={expanded
                  ? `${useDocumentScroll ? '' : 'min-h-0 flex-1'} overflow-x-auto overflow-y-hidden pr-0.5`
                  : useDocumentScroll ? 'space-y-1' : 'min-h-0 flex-1 space-y-1 overflow-y-auto'}
                verticalScroll={useDocumentScroll ? 'page' : 'column'}
                header={(
                  <KanbanStatusHeader
                    def={col.def}
                    label={col.def?.label || formatLabel(col.key)}
                    count={col.items.length}
                    metric={renderColumnTotal(col.items)}
                    progress={progress}
                    muted={sink}
                    action={!allCampaignSummaryMode && col.items.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => toggleExpandedStatus(col.key)}
                        aria-expanded={expanded}
                        title={expanded ? 'Collapse substatus lanes' : 'Expand substatus lanes'}
                        className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[9px] font-bold text-white/72 transition-colors hover:bg-white/[0.14] hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/50"
                      >
                        <Icon icon={expanded ? 'bx:collapse-horizontal' : 'bx:expand-horizontal'} className="h-3.5 w-3.5" />
                        {expanded ? 'Collapse' : 'Expand'}
                      </button>
                    ) : undefined}
                  />
                )}
              >
                {expanded ? (
                  <>
                    {visibleSubstatusBuckets.length > 0 ? (
                    <div className="flex min-h-full gap-2">
                      {visibleSubstatusBuckets.map((substatus) => (
                        <div key={substatus.key} className="flex min-h-[120px] min-w-0 flex-1 flex-col rounded-lg border border-white/[0.04] bg-black/[0.18] p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
                          <div
                            className="mb-1.5 flex items-start justify-between gap-1 rounded-md border border-white/[0.045] bg-white/[0.065] px-1.5 py-1.5"
                            style={getKanbanSubstatusHeaderStyle(col.def?.accentColor)}
                          >
                            <span className="flex min-w-0 items-start gap-1.5">
                              <span className="mt-0.5 rounded bg-white/[0.14] px-1 py-0.5 text-[8px] font-black tabular-nums text-white/70">{substatus.sequence}</span>
                              {substatus.icon && <Icon icon={substatus.icon} className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/72" />}
                              <span className="min-w-0">
                                <span className="block text-[7px] font-black uppercase tracking-[0.16em] text-white/45">Substatus</span>
                                <span className="block truncate text-[9px] font-black uppercase tracking-[0.08em] text-white/85">{substatus.label}</span>
                              </span>
                            </span>
                            <span className="rounded-md bg-white/[0.14] px-1.5 py-0.5 tabular-nums text-[9px] font-black text-white/80">{substatus.items.length}</span>
                          </div>
                          <div className={useDocumentScroll ? 'flex-1 space-y-1' : 'min-h-0 flex-1 space-y-1 overflow-y-auto'}>
                            {renderGroupedCards(substatus.items, false, `${col.key}:${substatus.key}`)}
                            {substatus.items.length === 0 && <div className="px-1 py-4 text-center text-[10px] font-semibold text-white/15">Empty</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                    ) : (
                      <div className="flex h-full items-center justify-center rounded-lg bg-black/16 px-2 py-6 text-center text-[11px] font-semibold text-white/20">Empty</div>
                    )}
                  </>
                ) : (
                  <>
                    {renderGroupedCards(shown, true, col.key)}
                    {hidden > 0 && (
                      <button
                        type="button"
                        onClick={() => setPageByCol((p) => ({ ...p, [col.key]: (p[col.key] || KANBAN_PAGE_SIZE) + KANBAN_PAGE_SIZE }))}
                        className="w-full rounded-lg px-2.5 py-2 text-center text-[11px] font-semibold text-white/45 transition-colors hover:bg-white/[0.05] hover:text-white/75"
                      >
                        Load more · {hidden}
                      </button>
                    )}
                    {col.items.length === 0 && <div className="px-2 py-6 text-center text-[11px] font-semibold text-white/20">Empty</div>}
                  </>
                )}
              </SchemaKanbanColumn>
            );
          }

          // DEFAULT column — standard card list (with optional week grouping).
          return (
            <SchemaKanbanColumn
              key={col.key}
              className={sink ? 'border-white/[0.04] bg-black/[0.28]' : 'border-white/[0.045] bg-black/[0.22]'}
              verticalScroll={useDocumentScroll ? 'page' : 'column'}
              header={<KanbanStatusHeader def={col.def} label={col.def?.label || formatLabel(col.key)} count={col.items.length} metric={renderColumnTotal(col.items)} progress={progress} muted={sink} />}
            >
              {renderGroupedCards(shown, true, col.key)}
              {hidden > 0 && (
                <button
                  type="button"
                  onClick={() => setPageByCol((p) => ({ ...p, [col.key]: (p[col.key] || KANBAN_PAGE_SIZE) + KANBAN_PAGE_SIZE }))}
                  className="w-full rounded-lg px-2.5 py-2 text-center text-[11px] font-semibold text-white/45 transition-colors hover:bg-white/[0.05] hover:text-white/75"
                >
                  Load more · {hidden}
                </button>
              )}
              {col.items.length === 0 && <div className="px-2 py-6 text-center text-[11px] font-semibold text-white/20">Empty</div>}
            </SchemaKanbanColumn>
          );
        })}
      </div>
    </div>
  );
};
